import { useState, useEffect, useCallback, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { sitesApi, type ApiSite, api, workspacesApi, ecomApi, promoApi, shippingApi, siteMembersApi, apiKeysApi, type ApiKey, type ApiProduct, type ApiOrder, type ApiPromoCode, type ApiShippingZone, galleryApi, type ApiGalleryItem, BASE, cloudApi, type CloudStatus, type CloudFile } from '../utils/api'
import { useEditorStore } from '../store/editor.store'
import { SITE_TEMPLATES } from '../data/siteTemplates'
import { HostingPanel } from '../components/HostingPanel'
import { FigmaPanel } from '../components/FigmaPanel'
import { BackendBuilderPanel } from '../components/BackendBuilderPanel'
import { NewProjectModal } from '../components/NewProjectModal'
import { ScreenshotCreateModal } from '../components/ScreenshotCreateModal'
import { SiteDashboardModal } from '../components/SiteDashboardModal'
import { FeatureFlagsPanel } from '../components/FeatureFlagsPanel'
import { ImportWebsiteModal } from '../components/ImportWebsiteModal'
import { ProjectsPanel } from '../components/ProjectsPanel'

interface SubscriptionInfo {
  plan: string
  effectivePlan?: string
  planExpiresAt?: string
  trialEndsAt?: string
  isOnTrial?: boolean
  hasActiveSubscription: boolean
}

const PLANS = [
  { key: 'FREE',     name: 'Free',     monthlyPrice: 0,    annualPrice: 0,    color: '#6b7280', features: ['1 сайт', 'Субдомен *.mybst.ru (SSL)', 'Базовые блоки', 'Брендинг BST'] },
  { key: 'PERSONAL', name: 'Personal', monthlyPrice: 790,  annualPrice: 590,  color: '#10b981', features: ['5 сайтов', 'Субдомен *.mybst.ru (SSL)', '1 кастомный домен (SSL)', 'Все блоки', 'Формы', 'Без брендинга'] },
  { key: 'BUSINESS', name: 'Business', monthlyPrice: 1990, annualPrice: 1490, color: '#5b6af0', features: ['10 сайтов', 'До 5 кастомных доменов (SSL)', 'CMS', 'E-commerce', 'Аналитика', 'Коллаборация (3)'], hot: true },
  { key: 'AGENCY',   name: 'Agency',   monthlyPrice: 4990, annualPrice: 3990, color: '#f59e0b', features: ['30 сайтов', 'До 30 кастомных доменов (SSL)', 'White Label', 'API', 'Коллаборация (10)', 'Клиентские кабинеты'] },
]

const HOSTING_PLANS = [
  { key: 'HOSTING_START',    name: 'Старт',  monthlyPrice: 149,  annualPrice: 112,  color: '#06b6d4', cpu: '1 vCPU',  ram: '512 МБ', nvme: '10 ГБ', bandwidth: '100 Мбит/с', features: ['SSL автоматически', 'Nginx', 'Ежедневные бэкапы'] },
  { key: 'HOSTING_BUSINESS', name: 'Бизнес', monthlyPrice: 299,  annualPrice: 224,  color: '#0ea5e9', cpu: '1 vCPU',  ram: '1 ГБ',   nvme: '20 ГБ', bandwidth: '200 Мбит/с', hot: true, features: ['SSL автоматически', 'Nginx · PHP 8.3', 'Ежедневные бэкапы', 'Мониторинг аптайма'] },
  { key: 'HOSTING_PRO',      name: 'Про',    monthlyPrice: 599,  annualPrice: 449,  color: '#6366f1', cpu: '2 vCPU', ram: '2 ГБ',   nvme: '40 ГБ', bandwidth: '500 Мбит/с', features: ['SSL + wildcard', 'CDN-кэширование', 'DDoS-защита L3/L4', 'Поддержка 24/7'] },
  { key: 'HOSTING_UNLIM',    name: 'Макс',   monthlyPrice: 1399, annualPrice: 1049, color: '#8b5cf6', cpu: '4 vCPU', ram: '8 ГБ',   nvme: '80 ГБ', bandwidth: '1 Гбит/с',  features: ['SSL Pro · wildcard CDN', 'DDoS Pro L7', 'SLA 99.9%', 'Выделенный IP'] },
]

const RECOMMENDER_QUESTIONS = [
  { id: 'sites', q: 'Сколько сайтов планируете создавать?', options: ['1 сайт', '2–5 сайтов', '6+ сайтов'] },
  { id: 'cms', q: 'Нужна ли CMS, е-commerce или аналитика?', options: ['Нет, только лендинг', 'Да, CMS / магазин', 'Да, и коллаборация с командой'] },
  { id: 'team', q: 'Работаете в команде?', options: ['Только я', '2–3 человека', '5+ человек / агентство'] },
]

function PricingModal({ current, onClose }: { current: string; onClose: () => void }) {
  const [loading, setLoading] = useState<string | null>(null)
  const [billing, setBilling] = useState<'month' | 'year'>('month')
  const [recommenderStep, setRecommenderStep] = useState<-1 | 0 | 1 | 2 | 3>(-1)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [recommended, setRecommended] = useState<string | null>(null)
  const isHostingPlan = current.startsWith('HOSTING')
  const [pricingTab, setPricingTab] = useState<'builder' | 'hosting'>(isHostingPlan ? 'hosting' : 'builder')

  const getDisplayPrice = (plan: typeof PLANS[0]) => {
    if (plan.monthlyPrice === 0) return 'Бесплатно'
    const price = billing === 'year' ? plan.annualPrice : plan.monthlyPrice
    return `${price.toLocaleString('ru')} ₽/мес`
  }

  const getAnnualTotal = (plan: typeof PLANS[0]) =>
    `${(plan.annualPrice * 12).toLocaleString('ru')} ₽/год`

  const handleUpgrade = async (planKey: string) => {
    setLoading(planKey)
    try {
      const res = await api.post<{ url: string }>('/payments/yukassa/checkout', {
        plan: planKey,
        returnUrl: `${window.location.origin}/dashboard?upgraded=1`,
        interval: billing,
      })
      if (res.url) window.location.href = res.url
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка. Попробуйте позже.')
      setLoading(null)
    }
  }

  const handleAnswer = (questionId: string, answerIdx: number) => {
    const newAnswers = { ...answers, [questionId]: answerIdx }
    setAnswers(newAnswers)
    if (recommenderStep < 2) {
      setRecommenderStep((recommenderStep + 1) as 0 | 1 | 2 | 3)
    } else {
      // Calculate recommendation
      const sitesAns = newAnswers['sites'] ?? 0   // 0=1site, 1=2-5, 2=6+
      const cmsAns = newAnswers['cms'] ?? 0        // 0=no, 1=cms, 2=team
      const teamAns = newAnswers['team'] ?? 0      // 0=solo, 1=2-3, 2=agency
      let rec = 'PERSONAL'
      if (sitesAns >= 2 || cmsAns >= 1 || teamAns >= 1) rec = 'BUSINESS'
      if (sitesAns >= 2 || teamAns >= 2) rec = 'AGENCY'
      setRecommended(rec)
      setRecommenderStep(3)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '20px', width: '900px', maxWidth: '100%', maxHeight: '92vh', overflow: 'auto', padding: '40px 32px', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: '24px', fontWeight: 800 }}>Тарифные планы</h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Текущий план: <strong>{current}</strong></p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#999', cursor: 'pointer', padding: '4px' }}>×</button>
        </div>

        {/* Category tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: '12px', padding: '4px', gap: '4px' }}>
            <button
              onClick={() => setPricingTab('builder')}
              style={{ padding: '8px 24px', borderRadius: '9px', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer', background: pricingTab === 'builder' ? '#fff' : 'transparent', color: pricingTab === 'builder' ? '#111' : '#666', boxShadow: pricingTab === 'builder' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none', transition: 'all 0.15s' }}
            >
              Конструктор
            </button>
            <button
              onClick={() => setPricingTab('hosting')}
              style={{ padding: '8px 24px', borderRadius: '9px', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer', background: pricingTab === 'hosting' ? '#fff' : 'transparent', color: pricingTab === 'hosting' ? '#111' : '#666', boxShadow: pricingTab === 'hosting' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none', transition: 'all 0.15s' }}
            >
              Облачный сервис
            </button>
          </div>
        </div>

        {/* Billing toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
          <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: '12px', padding: '4px', gap: '4px' }}>
            <button
              onClick={() => setBilling('month')}
              style={{ padding: '8px 20px', borderRadius: '9px', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer', background: billing === 'month' ? '#fff' : 'transparent', color: billing === 'month' ? '#111' : '#666', boxShadow: billing === 'month' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none', transition: 'all 0.15s' }}
            >
              Ежемесячно
            </button>
            <button
              onClick={() => setBilling('year')}
              style={{ padding: '8px 20px', borderRadius: '9px', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer', background: billing === 'year' ? '#5b6af0' : 'transparent', color: billing === 'year' ? '#fff' : '#666', boxShadow: billing === 'year' ? '0 1px 4px rgba(91,106,240,0.3)' : 'none', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              Ежегодно
              <span style={{ fontSize: '10px', background: billing === 'year' ? 'rgba(255,255,255,0.25)' : '#10b981', color: '#fff', padding: '1px 6px', borderRadius: '6px', fontWeight: 800 }}>-25%</span>
            </button>
          </div>
        </div>

        {/* Plan cards — Builder */}
        {pricingTab === 'builder' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {PLANS.map(plan => {
              const isCurrent = current === plan.key
              const isRecommended = recommended === plan.key
              return (
                <div key={plan.key} style={{ border: `2px solid ${isRecommended ? '#10b981' : isCurrent ? plan.color : (plan as any).hot ? plan.color : '#e5e7eb'}`, borderRadius: '16px', padding: '20px', position: 'relative', background: isRecommended ? '#f0fdf4' : isCurrent ? `${plan.color}10` : (plan as any).hot ? '#f0f1ff' : '#fff' }}>
                  {isRecommended && <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: '#10b981', color: '#fff', fontSize: '10px', fontWeight: 800, padding: '2px 12px', borderRadius: '10px', whiteSpace: 'nowrap' }}>Рекомендован</div>}
                  {!isRecommended && (plan as any).hot && <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#fff', fontSize: '10px', fontWeight: 800, padding: '2px 12px', borderRadius: '10px', whiteSpace: 'nowrap' }}>Популярный</div>}
                  {isCurrent && <div style={{ position: 'absolute', top: '-11px', right: '16px', background: plan.color, color: '#fff', fontSize: '10px', fontWeight: 800, padding: '2px 10px', borderRadius: '10px', whiteSpace: 'nowrap' }}>Текущий</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: plan.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#fff', fontWeight: 800 }}>{plan.name[0]}</div>
                    <span style={{ fontWeight: 800, fontSize: '16px' }}>{plan.name}</span>
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 900, color: plan.color, marginBottom: '2px' }}>{getDisplayPrice(plan)}</div>
                  {billing === 'year' && plan.annualPrice > 0 && (
                    <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, marginBottom: '12px' }}>{getAnnualTotal(plan)} — экономия {((plan.monthlyPrice - plan.annualPrice) * 12).toLocaleString('ru')} ₽</div>
                  )}
                  {billing === 'month' && plan.monthlyPrice > 0 && <div style={{ height: '16px', marginBottom: '12px' }} />}
                  <ul style={{ margin: '0 0 20px', padding: '0 0 0 16px', listStyle: 'disc', fontSize: '13px', color: '#444', lineHeight: 2 }}>
                    {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  {plan.key === 'FREE' ? (
                    isCurrent
                      ? <div style={{ width: '100%', padding: '10px', background: '#f3f4f6', borderRadius: '9px', fontSize: '13px', fontWeight: 700, color: '#888', textAlign: 'center' }}>Текущий тариф</div>
                      : <div style={{ width: '100%', padding: '10px', background: '#f3f4f6', borderRadius: '9px', fontSize: '13px', fontWeight: 600, color: '#999', textAlign: 'center' }}>Бесплатно</div>
                  ) : isCurrent ? (
                    <button onClick={() => handleUpgrade(plan.key)} disabled={!!loading} style={{ width: '100%', padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: '9px', fontSize: '13px', fontWeight: 700, color: '#333', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                      {loading === plan.key ? '...' : 'Продлить тариф →'}
                    </button>
                  ) : (
                    <button onClick={() => handleUpgrade(plan.key)} disabled={!!loading} style={{ width: '100%', padding: '10px', background: plan.color, color: '#fff', border: 'none', borderRadius: '9px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                      {loading === plan.key ? '...' : `Оплатить · ${getDisplayPrice(plan)}`}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Plan cards — Cloud */}
        {pricingTab === 'hosting' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {HOSTING_PLANS.map(plan => {
              const isCurrent = current === plan.key
              return (
                <div key={plan.key} style={{ border: `2px solid ${isCurrent ? plan.color : plan.hot ? plan.color : '#e5e7eb'}`, borderRadius: '16px', padding: '20px', position: 'relative', background: isCurrent ? `${plan.color}08` : plan.hot ? '#f0f9ff' : '#fff' }}>
                  {plan.hot && !isCurrent && <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#fff', fontSize: '10px', fontWeight: 800, padding: '2px 12px', borderRadius: '10px', whiteSpace: 'nowrap' }}>Популярный</div>}
                  {isCurrent && <div style={{ position: 'absolute', top: '-11px', right: '16px', background: plan.color, color: '#fff', fontSize: '10px', fontWeight: 800, padding: '2px 10px', borderRadius: '10px', whiteSpace: 'nowrap' }}>Текущий</div>}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: plan.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#fff', fontWeight: 800 }}>{plan.name[0]}</div>
                    <span style={{ fontWeight: 800, fontSize: '16px' }}>{plan.name}</span>
                  </div>

                  {/* CPU / RAM / NVMe */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1px', background: '#e2e8f0', borderRadius: '10px', overflow: 'hidden', marginBottom: '8px' }}>
                    {[{ v: plan.cpu, l: 'CPU' }, { v: plan.ram, l: 'RAM' }, { v: plan.nvme, l: 'NVMe' }].map(s => (
                      <div key={s.l} style={{ background: '#f8fafc', padding: '8px 4px', textAlign: 'center' }}>
                        <div style={{ fontWeight: 900, fontSize: '12px', color: '#111' }}>{s.v}</div>
                        <div style={{ fontSize: '9px', color: '#888', fontWeight: 700, marginTop: '2px', letterSpacing: '0.5px' }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', marginBottom: '14px', background: '#f1f5f9', borderRadius: '6px', padding: '4px 0', fontWeight: 600 }}>
                    Канал {plan.bandwidth}
                  </div>

                  <div style={{ fontSize: '20px', fontWeight: 900, color: plan.color, marginBottom: '2px' }}>{getDisplayPrice(plan)}</div>
                  {billing === 'year' && (
                    <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, marginBottom: '12px' }}>{getAnnualTotal(plan)} — экономия {((plan.monthlyPrice - plan.annualPrice) * 12).toLocaleString('ru')} ₽</div>
                  )}
                  {billing === 'month' && <div style={{ height: '16px', marginBottom: '12px' }} />}

                  <ul style={{ margin: '0 0 16px', padding: '0 0 0 16px', listStyle: 'disc', fontSize: '12px', color: '#444', lineHeight: 1.9 }}>
                    {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>

                  {isCurrent ? (
                    <button onClick={() => handleUpgrade(plan.key)} disabled={!!loading} style={{ width: '100%', padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: '9px', fontSize: '13px', fontWeight: 700, color: '#333', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                      {loading === plan.key ? '...' : 'Продлить →'}
                    </button>
                  ) : (
                    <button onClick={() => handleUpgrade(plan.key)} disabled={!!loading} style={{ width: '100%', padding: '10px', background: plan.color, color: '#fff', border: 'none', borderRadius: '9px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                      {loading === plan.key ? '...' : `Оплатить · ${getDisplayPrice(plan)}`}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Plan Recommender (builder only) */}
        {pricingTab === 'builder' && <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
          {recommenderStep === -1 ? (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => setRecommenderStep(0)}
                style={{ padding: '8px 20px', background: 'none', border: '1px solid #5b6af0', color: '#5b6af0', borderRadius: '9px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              >
                🎯 Подобрать тариф за 3 вопроса
              </button>
            </div>
          ) : recommenderStep === 3 ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                Рекомендуем вам тариф <strong style={{ color: PLANS.find(p => p.key === recommended)?.color }}>{recommended}</strong> — он соответствует вашим потребностям.
              </div>
              <button onClick={() => { setRecommenderStep(-1); setAnswers({}); setRecommended(null) }} style={{ padding: '6px 14px', background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px', color: '#666', cursor: 'pointer' }}>
                Сбросить
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '10px', textAlign: 'center' }}>
                Вопрос {recommenderStep + 1}/3: {RECOMMENDER_QUESTIONS[recommenderStep].q}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {RECOMMENDER_QUESTIONS[recommenderStep].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(RECOMMENDER_QUESTIONS[recommenderStep].id, i)}
                    style={{ padding: '8px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '9px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: '#333' }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>}

        <p style={{ margin: '16px 0 0', textAlign: 'center', fontSize: '12px', color: '#999' }}>
          Оплата через ЮКассу. Тариф активируется сразу после оплаты. Гарантия возврата 30 дней.
        </p>
      </div>
    </div>
  )
}

interface UserProfile {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
  plan: string
  createdAt: string
  isAdmin?: boolean
}

interface WorkspaceListItem {
  id: string
  role: 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER'
  workspace: {
    id: string
    name: string
    slug: string
    _count: { members: number; sites: number }
  }
}

interface WorkspaceMemberInfo {
  id: string
  role: 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER'
  user: { id: string; name: string | null; email: string; avatarUrl: string | null }
}

interface WorkspaceDetail {
  id: string
  name: string
  slug: string
  members: WorkspaceMemberInfo[]
  _count: { sites: number }
}

function ReferralTab() {
  const [info, setInfo] = useState<{ code: string; referredCount: number; referredBy: string | null } | null>(null)
  const [applyCode, setApplyCode] = useState('')
  const [applyStatus, setApplyStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    api.get<{ code: string; referredCount: number; referredBy: string | null }>('/users/me/referral').then(setInfo).catch(() => {})
  }, [])

  const refLink = info ? `${window.location.origin}/?ref=${info.code}` : ''

  const copyLink = () => {
    if (!refLink) return
    navigator.clipboard.writeText(refLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }

  const handleApply = async () => {
    if (!applyCode.trim()) return
    setApplyStatus('loading')
    try {
      await api.post('/users/me/referral/apply', { code: applyCode.trim().toUpperCase() })
      setApplyStatus('ok')
      const updated = await api.get<{ code: string; referredCount: number; referredBy: string | null }>('/users/me/referral')
      setInfo(updated)
    } catch {
      setApplyStatus('error')
    }
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '40px 0' }}>
      <h2 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 8px', color: '#111' }}>🎁 Реферальная программа</h2>
      <p style={{ fontSize: '14px', color: '#666', margin: '0 0 32px' }}>Приглашайте друзей и получайте бонусы. Каждый приглашённый пользователь засчитывается в вашу статистику.</p>

      {/* Referral link */}
      <div style={{ padding: '24px', background: 'linear-gradient(135deg,#f0f1ff,#e8eaff)', borderRadius: '16px', marginBottom: '24px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#5b6af0', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ваша реферальная ссылка</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, padding: '10px 14px', background: '#fff', border: '1px solid #dde1ff', borderRadius: '10px', fontSize: '13px', fontFamily: 'monospace', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {info ? refLink : 'Загрузка...'}
          </div>
          <button onClick={copyLink} style={{ padding: '10px 18px', background: copied ? '#10b981' : '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s' }}>
            {copied ? '✓ Скопировано' : 'Копировать'}
          </button>
        </div>
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
          Ваш код: <code style={{ background: '#fff', padding: '2px 8px', borderRadius: '6px', fontFamily: 'monospace', fontWeight: 700, color: '#5b6af0' }}>{info?.code ?? '...'}</code>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 900, color: '#5b6af0' }}>{info?.referredCount ?? 0}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Приглашено пользователей</div>
        </div>
        <div style={{ padding: '20px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', fontWeight: 900, color: '#10b981' }}>{info?.referredBy ? '✓' : '—'}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{info?.referredBy ? `Приглашён по коду ${info.referredBy}` : 'Не приглашён'}</div>
        </div>
      </div>

      {/* Social share */}
      {info && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            Поделиться ссылкой
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { icon: '✈', label: 'Telegram', bg: '#2aabee', url: `https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent('Попробуйте BST — лучший конструктор сайтов с AI! По моей ссылке бесплатно:')}` },
              { icon: '💬', label: 'WhatsApp', bg: '#25d366', url: `https://wa.me/?text=${encodeURIComponent('Попробуйте BST — конструктор сайтов с AI! ' + refLink)}` },
              { icon: '📘', label: 'VK',       bg: '#0077ff', url: `https://vk.com/share.php?url=${encodeURIComponent(refLink)}&title=${encodeURIComponent('Попробуйте BST — конструктор сайтов с AI!')}` },
              { icon: '𝕏',  label: 'Twitter',  bg: '#000',    url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent('Попробуйте BST — конструктор сайтов с AI! По реферальной ссылке:')}` },
            ].map(link => (
              <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', background: link.bg, color: '#fff', textDecoration: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700 }}>
                <span>{link.icon}</span>{link.label}
              </a>
            ))}
          </div>
          <div style={{ marginTop: '10px', padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '12px', color: '#555', lineHeight: 1.6 }}>
            🎁 <b>Бонус:</b> За каждого приглашённого пользователя, который перейдёт на платный план, вы получаете <b>+30 дней</b> к вашей подписке.
          </div>
        </div>
      )}

      {/* Apply code */}
      {!info?.referredBy && (
        <div style={{ padding: '20px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>Введите реферальный код</div>
          <p style={{ fontSize: '12px', color: '#888', margin: '0 0 12px' }}>Если кто-то пригласил вас, введите их код, чтобы засчитать реферал.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              value={applyCode}
              onChange={e => { setApplyStatus('idle'); setApplyCode(e.target.value.toUpperCase()) }}
              placeholder="ABCD1234"
              maxLength={10}
              style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', fontFamily: 'monospace', letterSpacing: '0.1em' }}
            />
            <button
              onClick={handleApply}
              disabled={applyStatus === 'loading' || !applyCode.trim()}
              style={{ padding: '10px 18px', background: applyStatus === 'ok' ? '#10b981' : '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: !applyCode.trim() ? 0.5 : 1 }}
            >
              {applyStatus === 'loading' ? '...' : applyStatus === 'ok' ? '✓ Применено' : 'Применить'}
            </button>
          </div>
          {applyStatus === 'error' && <div style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>Код не найден или уже использован</div>}
        </div>
      )}
    </div>
  )
}

function GalleryTab() {
  const [items, setItems] = useState<ApiGalleryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [activeTag, setActiveTag] = useState('')
  const [search, setSearch] = useState('')

  const load = useCallback(async (p: number, tag: string, qStr: string) => {
    setLoading(true)
    try {
      const res = await galleryApi.list({ page: p, tag: tag || undefined, q: qStr || undefined })
      setItems(res.items)
      setTotal(res.total)
      setPages(res.pages)
      setPage(res.page)
    } catch { /* noop */ } finally { setLoading(false) }
  }, [])

  useEffect(() => { load(1, activeTag, q) }, [load, activeTag, q])

  const TAGS = ['portfolio', 'landing', 'shop', 'blog', 'agency', 'restaurant', 'startup']

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>Вдохновение</h1>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Сайты, созданные сообществом BST. Включите показ своего сайта в Настройках → Публичная галерея.</p>
      </div>

      {/* Search + tag filter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setQ(search) }}
          placeholder="Найти сайт..."
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
        />
        <button
          onClick={() => setQ(search)}
          style={{ padding: '8px 16px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
        >
          Найти
        </button>
      </div>

      {/* Tag chips */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveTag('')}
          style={{ padding: '4px 12px', borderRadius: '20px', border: `1px solid ${!activeTag ? '#5b6af0' : '#e5e7eb'}`, background: !activeTag ? '#5b6af0' : '#fff', color: !activeTag ? '#fff' : '#555', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
        >
          Все
        </button>
        {TAGS.map(tag => (
          <button
            key={tag}
            onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
            style={{ padding: '4px 12px', borderRadius: '20px', border: `1px solid ${activeTag === tag ? '#5b6af0' : '#e5e7eb'}`, background: activeTag === tag ? '#5b6af0' : '#fff', color: activeTag === tag ? '#fff' : '#555', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
          >
            {tag}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⟳</div>
          <div>Загружаю...</div>
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 40px', color: '#888' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>{activeTag || q ? '🔍' : '🌐'}</div>
          {activeTag || q ? (
            <>
              <div style={{ fontWeight: 700, fontSize: '17px', color: '#111', marginBottom: '8px' }}>Ничего не найдено</div>
              <div style={{ fontSize: '14px' }}>Нет сайтов{activeTag ? ` с тегом «${activeTag}»` : q ? ` по запросу «${q}»` : ''}</div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: '17px', color: '#111', marginBottom: '10px' }}>Галерея пока пуста</div>
              <div style={{ fontSize: '14px', color: '#666', lineHeight: 1.8, maxWidth: '380px', margin: '0 auto 24px' }}>
                Станьте первым! Опубликуйте свой сайт и добавьте его в галерею BST — ваш сайт увидят все пользователи платформы.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', fontSize: '13px', color: '#999' }}>
                <span>Мои сайты → ⋮ → 🌍 Добавить в галерею</span>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>Найдено: {total}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            {items.map(site => (
              <a
                key={site.id}
                href={`https://${site.slug}.mybst.ru`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', textDecoration: 'none', transition: 'box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
              >
                {/* Preview placeholder */}
                <div style={{ height: '140px', background: 'linear-gradient(135deg, #667eea22, #764ba222)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>
                  {site.favicon ? (
                    <img src={site.favicon} alt="" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                  ) : '🌐'}
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#111', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{site.name}</div>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>{site.slug}.mybst.ru</div>
                  {site.publicListingTags.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {site.publicListingTags.slice(0, 3).map(tag => (
                        <span key={tag} style={{ padding: '2px 8px', background: '#f3f4f6', borderRadius: '10px', fontSize: '10px', color: '#555', fontWeight: 500 }}>{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <button onClick={() => load(page - 1, activeTag, q)} disabled={page <= 1} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff', cursor: page > 1 ? 'pointer' : 'default', color: page > 1 ? '#111' : '#ccc', fontSize: '13px', fontWeight: 600 }}>← Назад</button>
              <span style={{ padding: '6px 14px', fontSize: '13px', color: '#666' }}>Стр. {page} / {pages}</span>
              <button onClick={() => load(page + 1, activeTag, q)} disabled={page >= pages} style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff', cursor: page < pages ? 'pointer' : 'default', color: page < pages ? '#111' : '#ccc', fontSize: '13px', fontWeight: 600 }}>Вперёд →</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ApiKeysTab() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null)
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    setLoadError(null)
    apiKeysApi.list().then(setKeys).catch((e: unknown) => {
      setLoadError(e instanceof Error ? e.message : 'Не удалось загрузить API ключи')
    }).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const key = await apiKeysApi.create(newName.trim())
      setNewKeyValue((key as ApiKey & { key?: string }).key ?? key.id)
      setNewName('')
      load()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка создания ключа')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (id: string) => {
    setRevokeConfirm(id)
  }

  const confirmRevoke = async () => {
    const id = revokeConfirm
    if (!id) return
    setRevokeConfirm(null)
    try {
      await apiKeysApi.revoke(id)
      setKeys(prev => prev.filter(k => k.id !== id))
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  const copy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  const inpStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fff' }
  const btnStyle: React.CSSProperties = { padding: '8px 16px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>API ключи</h1>
        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Используйте API ключи для интеграции BST с внешними сервисами</p>
      </div>

      {newKeyValue && (
        <div style={{ background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '10px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#059669', marginBottom: '6px' }}>Новый API ключ создан — скопируйте сейчас, больше не будет показан:</div>
            <code style={{ fontFamily: 'monospace', fontSize: '12px', background: '#fff', padding: '6px 10px', borderRadius: '6px', wordBreak: 'break-all' }}>{newKeyValue}</code>
          </div>
          <button onClick={() => copy(newKeyValue, 'new')} style={{ ...btnStyle, background: '#10b981', flexShrink: 0 }}>
            {copiedId === 'new' ? '✓ Скопировано' : 'Копировать'}
          </button>
          <button onClick={() => setNewKeyValue(null)} style={{ background: 'none', border: 'none', fontSize: '18px', color: '#888', cursor: 'pointer' }}>×</button>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: '#111' }}>Создать новый ключ</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            style={{ ...inpStyle, flex: 1 }}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Название ключа (например: Zapier integration)"
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <button onClick={handleCreate} disabled={creating || !newName.trim()} style={{ ...btnStyle, opacity: creating || !newName.trim() ? 0.6 : 1 }}>
            {creating ? '...' : '+ Создать'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>⟳ Загрузка...</div>
      ) : loadError ? (
        <div style={{ textAlign: 'center', padding: '60px 40px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠</div>
          <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px', color: '#111' }}>Не удалось загрузить ключи</div>
          <div style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>{loadError}</div>
          <button onClick={load} style={{ padding: '9px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Повторить</button>
        </div>
      ) : keys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔑</div>
          <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '6px' }}>Нет API ключей</div>
          <div style={{ fontSize: '13px' }}>Создайте ключ для доступа к BST API из внешних сервисов</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {keys.map(key => (
            <div key={key.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#111', marginBottom: '2px' }}>{key.name}</div>
                <div style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace' }}>
                  {key.id.slice(0, 8)}••••••••
                  {key.expiresAt && <span style={{ marginLeft: '8px' }}>Истекает: {new Date(key.expiresAt).toLocaleDateString('ru')}</span>}
                </div>
              </div>
              <span style={{ fontSize: '11px', background: '#e0e7ff', color: '#5b6af0', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>Активен</span>
              <button
                onClick={() => copy(key.id, key.id)}
                style={{ padding: '5px 12px', background: copiedId === key.id ? '#10b981' : '#f3f4f6', color: copiedId === key.id ? '#fff' : '#333', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                {copiedId === key.id ? '✓' : 'Копировать'}
              </button>
              <button
                onClick={() => handleRevoke(key.id)}
                style={{ padding: '5px 12px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                Отозвать
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '32px', padding: '16px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '12px', color: '#666', lineHeight: 1.6 }}>
        <strong style={{ color: '#374151' }}>Документация по API:</strong> Используйте Bearer-токен в заголовке <code style={{ fontFamily: 'monospace', background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>Authorization: Bearer YOUR_KEY</code>. Базовый URL: <code style={{ fontFamily: 'monospace', background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>https://mybst.ru/api</code>
      </div>

      {revokeConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '28px 32px', maxWidth: '380px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '10px', color: '#111' }}>Отозвать ключ?</div>
            <div style={{ fontSize: '14px', color: '#555', marginBottom: '24px', lineHeight: 1.5 }}>
              Это действие необратимо. Все интеграции, использующие этот ключ, перестанут работать.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRevokeConfirm(null)}
                style={{ padding: '8px 20px', background: '#f3f4f6', color: '#333', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                Отмена
              </button>
              <button
                onClick={confirmRevoke}
                style={{ padding: '8px 20px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              >
                Отозвать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Changelog data ────────────────────────────────────────────────────────────

const CHANGELOG_LATEST_VERSION = '1.4'
const CHANGELOG: Array<{ version: string; date: string; entries: Array<{ type: 'new' | 'fix' | 'improve'; text: string }> }> = [
  {
    version: '1.4',
    date: '2 августа 2026',
    entries: [
      { type: 'new', text: '14-дневный пробный период Business для всех новых пользователей' },
      { type: 'new', text: 'Оплата через ЮКассу — SberPay, ЮMoney, карты РФ' },
      { type: 'new', text: 'Годовые тарифы со скидкой 25% — экономия до 2 месяцев' },
      { type: 'new', text: 'Помощник по выбору тарифа — ответьте на 3 вопроса' },
      { type: 'new', text: 'Чеклист начала работы на дашборде' },
      { type: 'new', text: 'Смена поддомена .mybst.ru из карточки сайта' },
      { type: 'improve', text: 'Лендинг обновлён: 376+ блоков, секция «Как это работает», статистика' },
    ],
  },
  {
    version: '1.3',
    date: '31 июля 2026',
    entries: [
      { type: 'new', text: '376+ готовых блоков — Hero, CTA, Testimonials, Timeline, Pricing и многое другое' },
      { type: 'new', text: '29 профессиональных шаблонов для разных ниш' },
      { type: 'new', text: 'Сохранённые блоки (компоненты) — переиспользуйте блоки между сайтами' },
      { type: 'new', text: 'Режим обслуживания и кастомная 404-страница' },
      { type: 'new', text: 'Реферальная программа — приводите друзей, получайте +30 дней' },
    ],
  },
  {
    version: '1.2',
    date: '18 июля 2026',
    entries: [
      { type: 'new', text: 'Онбординг-тур для новых пользователей' },
      { type: 'new', text: 'Комментарии на холсте в стиле Figma' },
      { type: 'new', text: 'Session recording через rrweb' },
      { type: 'new', text: 'Еженедельные email-отчёты по аналитике' },
      { type: 'new', text: 'AI-генерация изображений прямо в редакторе' },
    ],
  },
  {
    version: '1.1',
    date: '15 июля 2026',
    entries: [
      { type: 'new', text: 'Мультиязычность — 15 языков, AI-переводчик' },
      { type: 'new', text: 'Командный доступ к сайту — пригласите редактора или читателя по ссылке' },
      { type: 'new', text: 'Password-protected страницы' },
      { type: 'new', text: 'Кастомные домены — подключите любой домен' },
      { type: 'improve', text: 'История версий страниц — откат к любой точке' },
    ],
  },
]

// ── What's New modal ──────────────────────────────────────────────────────────

function WhatsNewModal({ onClose }: { onClose: () => void }) {
  const typeLabel: Record<string, string> = { new: 'Новое', fix: 'Исправлено', improve: 'Улучшено' }
  const typeColor: Record<string, string> = { new: '#5b6af0', fix: '#10b981', improve: '#f59e0b' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '540px', maxWidth: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.22)' }}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#111' }}>✨ Что нового</div>
            <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>История обновлений BST</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#999', cursor: 'pointer', padding: '4px' }}>×</button>
        </div>
        <div style={{ padding: '20px 28px' }}>
          {CHANGELOG.map((release, ri) => (
            <div key={release.version} style={{ marginBottom: ri < CHANGELOG.length - 1 ? '28px' : '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', fontWeight: 800, background: ri === 0 ? '#5b6af0' : '#f3f4f6', color: ri === 0 ? '#fff' : '#555', padding: '2px 10px', borderRadius: '20px' }}>v{release.version}</span>
                <span style={{ fontSize: '12px', color: '#888' }}>{release.date}</span>
                {ri === 0 && <span style={{ fontSize: '10px', fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '1px 6px', borderRadius: '4px' }}>LATEST</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {release.entries.map((entry, ei) => (
                  <div key={ei} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: typeColor[entry.type], background: `${typeColor[entry.type]}15`, padding: '2px 6px', borderRadius: '4px', marginTop: '1px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {typeLabel[entry.type]}
                    </span>
                    <span style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>{entry.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── NPS Survey ────────────────────────────────────────────────────────────────

function NpsSurvey({ profile, onDismiss }: { profile: UserProfile | null; onDismiss: () => void }) {
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  if (!profile) return null

  const daysSinceRegistration = (Date.now() - new Date(profile.createdAt).getTime()) / (1000 * 60 * 60 * 24)
  if (daysSinceRegistration < 30) return null

  const scoreColor = (s: number) => s <= 6 ? '#ef4444' : s <= 8 ? '#f59e0b' : '#10b981'

  async function handleSubmit() {
    if (score === null) return
    setLoading(true)
    try {
      await api.post('/users/me/nps', { score, comment: comment.trim() || undefined })
      setSubmitted(true)
      const uid = profile?.id ?? ''
      setTimeout(() => {
        localStorage.setItem(`bst-nps-${uid}`, new Date().toISOString())
        onDismiss()
      }, 2000)
    } catch {
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 8000, width: '340px', background: '#fff', borderRadius: '16px', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      <div style={{ background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: '#fff' }}>
          <div style={{ fontWeight: 800, fontSize: '15px' }}>Понравился BST?</div>
          <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>30 секунд — помогите нам стать лучше</div>
        </div>
        <button onClick={() => { localStorage.setItem(`bst-nps-${profile.id}`, 'dismissed'); onDismiss() }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '18px', cursor: 'pointer', padding: '2px' }}>×</button>
      </div>

      {submitted ? (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
          <div style={{ fontWeight: 700, color: '#111' }}>Спасибо за отзыв!</div>
          <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>Ваш ответ помогает нам улучшать продукт</div>
        </div>
      ) : (
        <div style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: '#374151', marginBottom: '12px', textAlign: 'center' }}>
            Насколько вероятно, что вы порекомендуете BST другу или коллеге?
          </div>
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginBottom: '10px' }}>
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                onClick={() => setScore(i)}
                style={{
                  width: '28px', height: '28px', border: `1px solid ${score === i ? scoreColor(i) : '#e5e7eb'}`,
                  borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                  background: score === i ? scoreColor(i) : '#f9fafb',
                  color: score === i ? '#fff' : '#555', transition: 'all 0.1s',
                }}
              >{i}</button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af', marginBottom: '12px' }}>
            <span>Точно нет</span><span>Обязательно</span>
          </div>
          {score !== null && (
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Что можно улучшить? (необязательно)"
              rows={2}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', resize: 'none', boxSizing: 'border-box', marginBottom: '10px', fontFamily: 'inherit' }}
            />
          )}
          <button
            onClick={handleSubmit}
            disabled={score === null || loading}
            style={{ width: '100%', padding: '10px', background: score !== null ? '#5b6af0' : '#e5e7eb', color: score !== null ? '#fff' : '#aaa', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: score !== null ? 'pointer' : 'default' }}
          >
            {loading ? '...' : 'Отправить оценку'}
          </button>
        </div>
      )}
    </div>
  )
}

function OnboardingChecklist({ sites, subscription, onUpgrade, onNewSite }: {
  sites: ApiSite[]
  subscription: SubscriptionInfo | null
  onUpgrade: () => void
  onNewSite: () => void
}) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('bst-onboarding-dismissed') === '1')

  const hasSite = sites.length > 0
  const hasPublished = sites.some(s => s.publishedAt || s.status === 'PUBLISHED')
  const hasCustomDomain = sites.some(s => s.customDomain)
  const hasUpgraded = !!(subscription && (subscription.plan !== 'FREE' || subscription.hasActiveSubscription))

  const steps = [
    { label: 'Создать аккаунт', done: true },
    { label: 'Создать первый сайт', done: hasSite, action: onNewSite, cta: 'Создать' },
    { label: 'Опубликовать сайт', done: hasPublished, hint: 'Нажмите «Опубликовать» в редакторе' },
    { label: 'Подключить свой домен', done: hasCustomDomain, hint: 'Настройки сайта → Домен' },
    { label: 'Перейти на платный тариф', done: hasUpgraded, action: onUpgrade, cta: 'Посмотреть тарифы' },
  ]

  const doneCount = steps.filter(s => s.done).length
  const allDone = doneCount === steps.length

  if (dismissed || allDone) return null

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px 24px', marginBottom: '28px', position: 'relative' }}>
      <button
        onClick={() => { localStorage.setItem('bst-onboarding-dismissed', '1'); setDismissed(true) }}
        style={{ position: 'absolute', top: '12px', right: '14px', background: 'none', border: 'none', fontSize: '18px', color: '#aaa', cursor: 'pointer', lineHeight: 1 }}
        title="Скрыть"
      >×</button>

      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
          <span style={{ fontSize: '15px', fontWeight: 800, color: '#111' }}>Начало работы</span>
          <span style={{ fontSize: '12px', color: '#5b6af0', fontWeight: 700 }}>{doneCount}/{steps.length}</span>
        </div>
        <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${(doneCount / steps.length) * 100}%`, background: 'linear-gradient(90deg,#5b6af0,#8b5cf6)', borderRadius: '2px', transition: 'width 0.4s' }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {steps.map((step) => (
          <div
            key={step.label}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: step.done ? '#f0fdf4' : '#f9fafb', border: `1px solid ${step.done ? '#86efac' : '#e5e7eb'}`, borderRadius: '9px', minWidth: '180px', flex: '1' }}
          >
            <span style={{ fontSize: '16px', flexShrink: 0, color: step.done ? '#10b981' : '#d1d5db' }}>
              {step.done ? '✓' : '○'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: step.done ? '#15803d' : '#374151', textDecoration: step.done ? 'line-through' : 'none' }}>
                {step.label}
              </div>
              {!step.done && step.hint && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>{step.hint}</div>
              )}
            </div>
            {!step.done && step.action && step.cta && (
              <button
                onClick={step.action}
                style={{ padding: '4px 10px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {step.cta}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const initFromApiSite = useEditorStore(s => s.initFromApiSite)
  const setReadOnly = useEditorStore(s => s.setReadOnly)
  const [sites, setSites] = useState<ApiSite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [editingOpen, setEditingOpen] = useState<string | null>(null)
  const [pricingOpen, setPricingOpen] = useState(false)
  const [aiCreateOpen, setAiCreateOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [tab, setTab] = useState<'sites' | 'shared' | 'workspaces' | 'api-keys' | 'referral' | 'gallery' | 'cloud' | 'hosting' | 'figma' | 'backend' | 'projects' | 'feature-flags'>('sites')
  const [featureFlagsOpen, setFeatureFlagsOpen] = useState(false)
  const [importWebsiteOpen, setImportWebsiteOpen] = useState(false)
  const [screenshotOpen, setScreenshotOpen] = useState(false)
  const [dashboardSite, setDashboardSite] = useState<ApiSite | null>(null)
  const [cloudStatus, setCloudStatus] = useState<CloudStatus | null>(null)
  const [cloudFiles, setCloudFiles] = useState<CloudFile[]>([])
  const [cloudLoading, setCloudLoading] = useState(false)
  const [cloudUploadDrag, setCloudUploadDrag] = useState(false)
  const [sharedSites, setSharedSites] = useState<(ApiSite & { memberRole: 'EDITOR' | 'VIEWER' })[]>([])
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([])
  const [workspacesLoading, setWorkspacesLoading] = useState(false)
  const [workspacesError, setWorkspacesError] = useState<string | null>(null)
  const [manageWorkspace, setManageWorkspace] = useState<WorkspaceListItem | null>(null)
  const [createWsOpen, setCreateWsOpen] = useState(false)
  const [shopSite, setShopSite] = useState<ApiSite | null>(null)
  const [planUsage, setPlanUsage] = useState<{ plan: string; planName: string; limit: number; used: number; canCreate: boolean } | null>(null)
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const [npsVisible, setNpsVisible] = useState(false)
  const [uploadSiteId, setUploadSiteId] = useState<string | null>(null)
  const [impersonatedEmail] = useState<string | null>(() => sessionStorage.getItem('bst-admin-email'))

  const changelogSeenVersion = typeof window !== 'undefined' ? localStorage.getItem('bst-changelog-seen') : CHANGELOG_LATEST_VERSION
  const hasUnseenChangelog = changelogSeenVersion !== CHANGELOG_LATEST_VERSION

  useEffect(() => {
    window.scrollTo(0, 0)
    document.documentElement.classList.remove('dark')
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  useEffect(() => {
    api.get<SubscriptionInfo>('/payments/subscription').then(s => {
      setSubscription(s)
      if (s?.plan?.startsWith('HOSTING_')) {
        cloudApi.status().then(setCloudStatus).catch(() => {})
      }
    }).catch(() => {})
    api.get<UserProfile>('/users/me').then(p => {
      if (!p) return
      setProfile(p)
      const npsKey = `bst-nps-${p.id}`
      const npsStored = localStorage.getItem(npsKey)
      const daysSince = (Date.now() - new Date(p.createdAt).getTime()) / (1000 * 60 * 60 * 24)
      if (!npsStored && daysSince >= 30) setNpsVisible(true)
    }).catch(() => {})
    sitesApi.getPlanUsage().then(setPlanUsage).catch(() => {})
  }, [])

  const fetchSites = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await sitesApi.list()
      setSites(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSites() }, [fetchSites])

  useEffect(() => {
    siteMembersApi.getSharedSites().then(setSharedSites).catch(() => setSharedSites([]))
  }, [])

  const fetchWorkspaces = useCallback(async () => {
    setWorkspacesLoading(true)
    setWorkspacesError(null)
    workspacesApi.list().then(data => setWorkspaces(data as WorkspaceListItem[])).catch((e: unknown) => {
      setWorkspacesError(e instanceof Error ? e.message : 'Не удалось загрузить рабочие пространства')
    }).finally(() => setWorkspacesLoading(false))
  }, [])

  useEffect(() => {
    if (tab === 'workspaces') fetchWorkspaces()
  }, [tab, fetchWorkspaces])

  async function handleEditSite(site: ApiSite) {
    setReadOnly(false)
    try {
      const fullSite = await sitesApi.get(site.id)
      initFromApiSite(fullSite)
      localStorage.setItem('bst-site-id', site.id)
      localStorage.setItem('bst-site-slug', site.slug)
      navigate('/editor')
    } catch {
      initFromApiSite(site)
      localStorage.setItem('bst-site-id', site.id)
      localStorage.setItem('bst-site-slug', site.slug)
      navigate('/editor')
    }
  }

  async function handleDeleteSite(siteId: string) {
    try {
      await sitesApi.delete(siteId)
      setSites(prev => prev.filter(s => s.id !== siteId))
      if (localStorage.getItem('bst-site-id') === siteId) {
        localStorage.removeItem('bst-site-id')
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка удаления')
    } finally {
      setDeleteConfirm(null)
    }
  }

  function handleNewSite() {
    setNewProjectOpen(true)
  }

  async function handleImportJson() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      try {
        const text = await file.text()
        const data = JSON.parse(text)
        if (!data.bstVersion || !data.site || !data.pages) {
          alert('Неверный формат файла BST backup JSON')
          return
        }
        const res = await api.post<ApiSite>('/sites/import-json', data)
        setSites(prev => [res, ...prev])
        alert(`Сайт «${res.name}» успешно импортирован!`)
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Ошибка импорта')
      }
    }
    input.click()
  }

  function logout() {
    localStorage.removeItem('bst-auth')
    localStorage.removeItem('bst-token')
    navigate('/', { replace: true })
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-2, #f4f5f7)', fontFamily: '-apple-system,sans-serif' }}>
      {/* Admin impersonation banner */}
      {impersonatedEmail && (
        <div style={{ background: '#ef4444', color: '#fff', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', fontWeight: 600 }}>
          <span>⚠ Режим имитации: вы просматриваете дашборд как <strong>{impersonatedEmail}</strong></span>
          <button
            onClick={() => {
              const adminToken = sessionStorage.getItem('bst-admin-token') ?? ''
              sessionStorage.removeItem('bst-admin-token')
              sessionStorage.removeItem('bst-admin-email')
              if (adminToken) {
                localStorage.setItem('bst-token', adminToken)
                localStorage.setItem('bst-auth', 'true')
              }
              window.location.href = '/admin'
            }}
            style={{ marginLeft: 'auto', padding: '5px 14px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '7px', color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
          >← Вернуться в Admin</button>
        </div>
      )}
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px', minWidth: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontSize: '22px', fontWeight: 900, letterSpacing: '-1px' }}>BST</span>
          <span style={{ fontSize: '10px', fontWeight: 700, background: '#5b6af0', color: '#fff', padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.05em' }}>beta</span>
        </div>
        <nav style={{ display: 'flex', gap: '4px', alignItems: 'center', overflowX: 'auto', flexShrink: 1, minWidth: 0, scrollbarWidth: 'none' }}>
          {subscription && (
            <button
              onClick={() => setPricingOpen(true)}
              style={{ padding: '5px 12px', background: subscription.isOnTrial ? '#f59e0b' : subscription.plan === 'FREE' ? '#f3f4f6' : '#5b6af0', color: subscription.isOnTrial ? '#fff' : subscription.plan === 'FREE' ? '#666' : '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
            >
              {subscription.isOnTrial ? 'Business Trial ⏳' : subscription.plan === 'FREE' ? 'Free · Upgrade ↑' : `${subscription.plan} ✓`}
            </button>
          )}
          <button
            onClick={() => { setWhatsNewOpen(true); localStorage.setItem('bst-changelog-seen', CHANGELOG_LATEST_VERSION) }}
            title="Что нового"
            style={{ position: 'relative', padding: '7px 12px', background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', color: '#555', cursor: 'pointer' }}
          >
            ✨ Обновления
            {hasUnseenChangelog && (
              <span style={{ position: 'absolute', top: '4px', right: '4px', width: '7px', height: '7px', background: '#ef4444', borderRadius: '50%', border: '1px solid #fff' }} />
            )}
          </button>
          <button
            onClick={() => setAiCreateOpen(true)}
            style={{ padding: '7px 14px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
          >
            ✨ AI сайт
          </button>
          {profile?.isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              style={{ padding: '7px 12px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
            >
              ⚙ Admin
            </button>
          )}
          <button
            onClick={handleImportJson}
            title="Импорт сайта из BST backup JSON"
            style={{ padding: '7px 12px', background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', color: '#555', cursor: 'pointer' }}
          >
            ⬆ Импорт
          </button>
          <button
            onClick={handleNewSite}
            style={{ padding: '7px 16px', background: '#111', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
          >
            + Новый сайт
          </button>
          <button
            onClick={() => setProfileOpen(true)}
            title={profile?.email ?? 'Профиль'}
            style={{ width: 32, height: 32, borderRadius: '50%', background: '#5b6af0', border: 'none', cursor: 'pointer', color: '#fff', fontWeight: 800, fontSize: 13, flexShrink: 0, marginLeft: '4px' }}
          >
            {profile?.name ? profile.name[0].toUpperCase() : profile?.email?.[0]?.toUpperCase() ?? 'U'}
          </button>
          <button
            onClick={logout}
            style={{ padding: '7px 12px', background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', color: '#555', cursor: 'pointer', marginLeft: '4px' }}
          >
            Выйти
          </button>
        </nav>
      </header>

      {/* Tab bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', overflowX: 'auto', scrollbarWidth: 'none' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', minWidth: 'max-content' }}>
          {(['sites', 'shared', 'workspaces', 'api-keys', 'referral', 'gallery', ...(subscription?.plan?.startsWith('HOSTING_') ? ['cloud' as const] : []), 'hosting' as const, 'figma' as const, 'backend' as const, 'projects' as const, 'feature-flags' as const] as const).map(t => (
            <button
              key={t}
              onClick={() => {
                setTab(t)
                if (t === 'cloud' && !cloudStatus) {
                  cloudApi.status().then(s => { setCloudStatus(s); if (s) cloudApi.files().then(setCloudFiles).catch(() => {}) }).catch(() => {})
                }
              }}
              style={{
                padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '14px', fontWeight: 600, transition: 'all 0.15s',
                borderBottom: `2px solid ${tab === t ? '#5b6af0' : 'transparent'}`,
                color: tab === t ? '#5b6af0' : '#666',
                marginBottom: '-1px',
              }}
            >
              {t === 'sites' ? 'Мои сайты' : t === 'shared' ? `Доступные${sharedSites.length > 0 ? ` (${sharedSites.length})` : ''}` : t === 'workspaces' ? 'Рабочие пространства' : t === 'api-keys' ? '🔑 API ключи' : t === 'referral' ? '🎁 Реферальная программа' : t === 'cloud' ? '☁️ Облачный сервис' : t === 'hosting' ? '🚀 Code Hosting' : t === 'figma' ? '🎨 Figma Import' : t === 'backend' ? '🗄️ Backend' : t === 'projects' ? '📁 Проекты' : t === 'feature-flags' ? '🚩 Feature Flags' : '🌍 Вдохновение'}
            </button>
          ))}
        </div>
      </div>

      {/* Trial banner */}
      {subscription?.isOnTrial && subscription.trialEndsAt && (() => {
        const daysLeft = Math.max(0, Math.ceil((new Date(subscription.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        return (
          <div style={{ background: 'linear-gradient(135deg,#f59e0b,#f97316)', color: '#fff', padding: '12px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>⏳</span>
              <div>
                <span style={{ fontWeight: 800, fontSize: '14px' }}>Пробный период Business</span>
                <span style={{ fontSize: '13px', opacity: 0.9, marginLeft: '8px' }}>
                  {daysLeft === 0 ? 'Заканчивается сегодня!' : `Осталось ${daysLeft} ${daysLeft === 1 ? 'день' : daysLeft < 5 ? 'дня' : 'дней'}`}
                </span>
              </div>
            </div>
            <button
              onClick={() => setPricingOpen(true)}
              style={{ padding: '7px 18px', background: '#fff', color: '#f59e0b', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Купить Business
            </button>
          </div>
        )
      })()}

      {/* Content */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' }}>
        {tab === 'sites' ? (
          <>
            <div style={{ marginBottom: '32px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <div>
                  <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>Мои сайты</h1>
                  <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                    {sites.length > 0 ? `${sites.length} сайт${sites.length === 1 ? '' : sites.length < 5 ? 'а' : 'ов'}` : 'Создайте первый сайт'}
                  </p>
                </div>
                <button
                  onClick={planUsage && !planUsage.canCreate ? () => setPricingOpen(true) : handleNewSite}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '10px 20px',
                    background: planUsage && !planUsage.canCreate ? '#f3f4f6' : '#111',
                    color: planUsage && !planUsage.canCreate ? '#9ca3af' : '#fff',
                    border: 'none', borderRadius: '10px',
                    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                    transition: 'opacity .15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  title={planUsage && !planUsage.canCreate ? 'Лимит сайтов исчерпан — обновите тариф' : 'Создать новый сайт'}
                >
                  + Создать сайт
                </button>
              </div>
              {planUsage && (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 16px', minWidth: '200px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Тариф: {planUsage.planName}</span>
                    <span style={{ fontSize: '11px', color: planUsage.canCreate ? '#6b7280' : '#ef4444', fontWeight: 600 }}>
                      {planUsage.used}/{planUsage.limit < 0 ? '∞' : planUsage.limit} сайтов
                    </span>
                  </div>
                  {planUsage.limit > 0 && (
                    <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.min(100, (planUsage.used / planUsage.limit) * 100)}%`,
                        background: planUsage.used >= planUsage.limit ? '#ef4444' : '#5b6af0',
                        borderRadius: '2px', transition: 'width 0.4s',
                      }} />
                    </div>
                  )}
                  {!planUsage.canCreate && (
                    <button onClick={() => setPricingOpen(true)} style={{ marginTop: '8px', width: '100%', padding: '5px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                      Обновить тариф ↑
                    </button>
                  )}
                </div>
              )}
            </div>

            {!loading && (
              <OnboardingChecklist
                sites={sites}
                subscription={subscription}
                onUpgrade={() => setPricingOpen(true)}
                onNewSite={() => setCreateOpen(true)}
              />
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⟳</div>
                Загрузка...
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>✕</div>
                {error}
                <button onClick={fetchSites} style={{ display: 'block', margin: '16px auto 0', padding: '8px 16px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                  Повторить
                </button>
              </div>
            ) : sites.length === 0 ? (
              <EmptyState onNew={handleNewSite} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: '20px' }}>
                {sites.map(site => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    userPlan={planUsage?.plan ?? 'FREE'}
                    onEdit={() => handleEditSite(site)}
                    onUpload={() => setUploadSiteId(site.id)}
                    onDelete={() => setDeleteConfirm(site.id)}
                    onRename={() => setEditingOpen(site.id)}
                    onShop={() => setShopSite(site)}
                    onPublish={async () => {
                      await sitesApi.publish(site.id)
                      setSites(prev => prev.map(s => s.id === site.id ? { ...s, status: 'PUBLISHED' } : s))
                    }}
                    onUnpublish={async () => {
                      await sitesApi.unpublish(site.id)
                      setSites(prev => prev.map(s => s.id === site.id ? { ...s, status: 'DRAFT' } : s))
                    }}
                    onClone={async () => {
                      try {
                        const cloned = await sitesApi.clone(site.id)
                        if (cloned) setSites(prev => [cloned, ...prev])
                      } catch (e) {
                        alert(e instanceof Error ? e.message : 'Ошибка клонирования')
                      }
                    }}
                    onGalleryToggle={async () => {
                      const updated = await sitesApi.update(site.id, { publiclyListed: !site.publiclyListed })
                      setSites(prev => prev.map(s => s.id === site.id ? { ...s, publiclyListed: updated.publiclyListed } : s))
                    }}
                    onDashboard={() => setDashboardSite(site)}
                    onPublishAsTemplate={async () => {
                      try {
                        await sitesApi.publishAsTemplate(site.id)
                        alert('Сайт опубликован как шаблон в галерее!')
                      } catch (e) {
                        alert(e instanceof Error ? e.message : 'Ошибка публикации шаблона')
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : tab === 'shared' ? (
          <>
            <div style={{ marginBottom: '32px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>Доступные сайты</h1>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Сайты, к которым вам предоставлен доступ другими пользователями</p>
            </div>
            {sharedSites.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>👥</div>
                <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '16px' }}>Нет доступных сайтов</div>
                <div style={{ fontSize: '14px' }}>Попросите владельца сайта пригласить вас через меню «Доступ»</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                {sharedSites.map(site => (
                  <div key={site.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                    onClick={() => {
                      initFromApiSite({ ...site, pages: site.pages ?? [] })
                      setReadOnly(site.memberRole === 'VIEWER')
                      localStorage.setItem('bst-site-id', site.id)
                      localStorage.setItem('bst-site-slug', site.slug)
                      navigate('/editor')
                    }}
                  >
                    <div style={{ height: '120px', background: 'linear-gradient(135deg,#e0e7ff,#ddd6fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>
                      {site.favicon || '🌐'}
                    </div>
                    <div style={{ padding: '16px' }}>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#111', marginBottom: '4px' }}>{site.name}</div>
                      <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>/{site.slug}</span>
                        <span style={{ background: '#e0e7ff', color: '#5b6af0', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                          {site.memberRole === 'EDITOR' ? '✏ Редактор' : '👁 Просмотр'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : tab === 'api-keys' ? (
          <ApiKeysTab />
        ) : tab === 'referral' ? (
          <ReferralTab />
        ) : tab === 'gallery' ? (
          <GalleryTab />
        ) : tab === 'cloud' ? (
          <>
            <div style={{ marginBottom: '32px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>Облачный сервис</h1>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Ваше персональное облачное хранилище для статичных файлов и сайтов</p>
            </div>

            {cloudLoading ? (
              <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⟳</div>
                Загрузка...
              </div>
            ) : !cloudStatus ? (
              <div style={{ textAlign: 'center', padding: '80px 40px' }}>
                <div style={{ fontSize: '56px', marginBottom: '16px' }}>☁️</div>
                <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px', color: '#111' }}>Облако ещё разворачивается</h2>
                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 8px' }}>Обычно это занимает 1–2 минуты после оплаты.</p>
                <button
                  onClick={() => { setCloudLoading(true); cloudApi.status().then(s => { setCloudStatus(s); if (s) cloudApi.files().then(setCloudFiles).catch(() => {}) }).catch(() => {}).finally(() => setCloudLoading(false)) }}
                  style={{ padding: '10px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Обновить статус
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '800px' }}>

                {/* Status card */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '28px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: '#888', marginBottom: '6px', fontWeight: 500 }}>Адрес вашего облака</div>
                      <a
                        href={`https://${cloudStatus.subdomain}.mybst.ru`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '20px', fontWeight: 800, color: '#5b6af0', textDecoration: 'none' }}
                      >
                        {cloudStatus.subdomain}.mybst.ru ↗
                      </a>
                    </div>
                    <span style={{
                      padding: '5px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: 700,
                      background: cloudStatus.status === 'active' ? '#d1fae5' : '#fee2e2',
                      color: cloudStatus.status === 'active' ? '#065f46' : '#991b1b',
                    }}>
                      {cloudStatus.status === 'active' ? '● Активен' : '● Приостановлен'}
                    </span>
                  </div>

                  {/* Specs grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                    {[
                      { label: 'CPU', value: cloudStatus.specs?.cpus !== undefined ? `${cloudStatus.specs.cpus} vCPU` : '—' },
                      { label: 'RAM', value: cloudStatus.specs?.memoryGb !== undefined ? `${cloudStatus.specs.memoryGb} ГБ` : '—' },
                      { label: 'NVMe', value: cloudStatus.specs?.diskGb !== undefined ? `${cloudStatus.specs.diskGb} ГБ` : '—' },
                      { label: 'Скорость', value: cloudStatus.specs?.bandwidth ?? '—' },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: '#111' }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Disk usage */}
                  {cloudStatus.specs?.diskGb && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                        <span>Использовано места</span>
                        <span style={{ fontWeight: 700, color: '#111' }}>
                          {cloudStatus.diskUsedMb < 1024
                            ? `${cloudStatus.diskUsedMb} МБ`
                            : `${(cloudStatus.diskUsedMb / 1024).toFixed(1)} ГБ`
                          } / {cloudStatus.specs.diskGb} ГБ
                        </span>
                      </div>
                      <div style={{ height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          borderRadius: '4px',
                          background: (() => {
                            const pct = cloudStatus.diskUsedMb / (cloudStatus.specs!.diskGb * 1024)
                            return pct > 0.9 ? '#ef4444' : pct > 0.7 ? '#f59e0b' : '#5b6af0'
                          })(),
                          width: `${Math.min(100, (cloudStatus.diskUsedMb / (cloudStatus.specs.diskGb * 1024)) * 100).toFixed(1)}%`,
                          transition: 'width 0.3s',
                        }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* File manager */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#111' }}>Файловый менеджер</h2>
                    <span style={{ fontSize: '13px', color: '#888' }}>{cloudFiles.length} файл{cloudFiles.length === 1 ? '' : cloudFiles.length < 5 ? 'а' : 'ов'}</span>
                  </div>

                  {/* Upload zone */}
                  <div
                    onDragOver={e => { e.preventDefault(); setCloudUploadDrag(true) }}
                    onDragLeave={() => setCloudUploadDrag(false)}
                    onDrop={async e => {
                      e.preventDefault()
                      setCloudUploadDrag(false)
                      const files = Array.from(e.dataTransfer.files)
                      for (const file of files) {
                        try {
                          await cloudApi.uploadFile(file)
                        } catch {}
                      }
                      cloudApi.files().then(setCloudFiles).catch(() => {})
                      cloudApi.status().then(setCloudStatus).catch(() => {})
                    }}
                    onClick={() => {
                      const inp = document.createElement('input')
                      inp.type = 'file'
                      inp.multiple = true
                      inp.onchange = async () => {
                        const files = Array.from(inp.files ?? [])
                        for (const file of files) {
                          try { await cloudApi.uploadFile(file) } catch {}
                        }
                        cloudApi.files().then(setCloudFiles).catch(() => {})
                        cloudApi.status().then(setCloudStatus).catch(() => {})
                      }
                      inp.click()
                    }}
                    style={{
                      border: `2px dashed ${cloudUploadDrag ? '#5b6af0' : '#d1d5db'}`,
                      borderRadius: '12px',
                      padding: '32px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      marginBottom: '20px',
                      background: cloudUploadDrag ? '#eef0ff' : '#fafafa',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '4px' }}>Перетащите файлы сюда или нажмите для выбора</div>
                    <div style={{ fontSize: '13px', color: '#888' }}>Максимум 50 МБ на файл · HTML, CSS, JS, изображения, шрифты</div>
                  </div>

                  {/* File list */}
                  {cloudFiles.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#aaa', fontSize: '14px' }}>
                      Файлов пока нет. Загрузите index.html чтобы опубликовать сайт.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {cloudFiles.map(f => (
                        <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f9fafb', borderRadius: '10px' }}>
                          <span style={{ fontSize: '18px' }}>
                            {/\.(html?)$/i.test(f.name) ? '📄' : /\.(css)$/i.test(f.name) ? '🎨' : /\.(js)$/i.test(f.name) ? '⚙️' : /\.(png|jpe?g|gif|webp|svg|ico)$/i.test(f.name) ? '🖼' : /\.(woff2?|ttf|otf|eot)$/i.test(f.name) ? '🔤' : '📎'}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>
                              {f.size < 1024 ? `${f.size} Б` : f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(1)} КБ` : `${(f.size / 1024 / 1024).toFixed(2)} МБ`}
                              {' · '}
                              {new Date(f.mtime).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          </div>
                          <a
                            href={`https://${cloudStatus.subdomain}.mybst.ru/${f.name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '13px', color: '#5b6af0', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}
                            onClick={e => e.stopPropagation()}
                          >
                            Открыть ↗
                          </a>
                          <button
                            onClick={async () => {
                              if (!confirm(`Удалить файл «${f.name}»?`)) return
                              try {
                                await cloudApi.deleteFile(f.name)
                                setCloudFiles(prev => prev.filter(x => x.name !== f.name))
                                cloudApi.status().then(setCloudStatus).catch(() => {})
                              } catch (e) {
                                alert(e instanceof Error ? e.message : 'Ошибка удаления')
                              }
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#ef4444', padding: '4px', flexShrink: 0 }}
                            title="Удалить"
                          >
                            🗑
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}
          </>
        ) : tab === 'hosting' ? (
          <HostingPanel />
        ) : tab === 'figma' ? (
          <FigmaPanel />
        ) : tab === 'backend' ? (
          <BackendBuilderPanel />
        ) : tab === 'projects' ? (
          <ProjectsPanel />
        ) : tab === 'feature-flags' ? (
          <div style={{ padding: '32px 0' }}>
            <div style={{ marginBottom: '24px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>🚩 Feature Flags</h1>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Управление флагами функциональности платформы</p>
            </div>
            <button
              onClick={() => setFeatureFlagsOpen(true)}
              style={{ padding: '12px 24px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
            >
              Открыть Feature Flags
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
              <div>
                <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>Рабочие пространства</h1>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                  {workspaces.length > 0 ? `${workspaces.length} пространств` : 'Создайте рабочее пространство для команды'}
                </p>
              </div>
              <button
                onClick={() => setCreateWsOpen(true)}
                style={{ padding: '9px 18px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              >
                + Создать
              </button>
            </div>

            {workspacesLoading ? (
              <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⟳</div>
                Загрузка...
              </div>
            ) : workspacesError ? (
              <div style={{ textAlign: 'center', padding: '80px 40px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠</div>
                <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px', color: '#111' }}>Не удалось загрузить рабочие пространства</div>
                <div style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>{workspacesError}</div>
                <button onClick={fetchWorkspaces} style={{ padding: '9px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Повторить</button>
              </div>
            ) : workspaces.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 40px' }}>
                <div style={{ fontSize: '56px', marginBottom: '16px' }}>👥</div>
                <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px', color: '#111' }}>Нет рабочих пространств</h2>
                <p style={{ fontSize: '14px', color: '#666', margin: '0 0 28px' }}>Создайте пространство и пригласите команду для совместной работы</p>
                <button
                  onClick={() => setCreateWsOpen(true)}
                  style={{ padding: '11px 24px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Создать рабочее пространство
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {workspaces.map(item => (
                  <WorkspaceRow
                    key={item.id}
                    item={item}
                    onManage={() => setManageWorkspace(item)}
                    onDelete={async () => {
                      if (!confirm(`Удалить пространство «${item.workspace.name}»? Это действие нельзя отменить.`)) return
                      try {
                        await workspacesApi.delete(item.workspace.id)
                        setWorkspaces(prev => prev.filter(w => w.id !== item.id))
                      } catch (e) {
                        alert(e instanceof Error ? e.message : 'Ошибка удаления')
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* AI create site modal */}
      {newProjectOpen && (
        <NewProjectModal
          onClose={() => setNewProjectOpen(false)}
          onGenerateAI={() => setAiCreateOpen(true)}
          onFigma={() => setTab('figma')}
          onScratch={() => setCreateOpen(true)}
          onScreenshot={() => setScreenshotOpen(true)}
          onImportUrl={() => setImportWebsiteOpen(true)}
        />
      )}

      {screenshotOpen && (
        <ScreenshotCreateModal
          onClose={() => setScreenshotOpen(false)}
          onCreated={() => { setScreenshotOpen(false) }}
        />
      )}

      {dashboardSite && (
        <SiteDashboardModal
          site={dashboardSite}
          onClose={() => setDashboardSite(null)}
          onOpenEditor={() => { initFromApiSite(dashboardSite); navigate('/editor') }}
          onOpenCms={() => { initFromApiSite(dashboardSite); navigate('/editor') }}
          onOpenSettings={() => { initFromApiSite(dashboardSite); navigate('/editor') }}
        />
      )}
      {featureFlagsOpen && <FeatureFlagsPanel onClose={() => setFeatureFlagsOpen(false)} />}
      {importWebsiteOpen && (
        <ImportWebsiteModal
          onClose={() => setImportWebsiteOpen(false)}
          onCreated={(_siteId, _slug) => { setImportWebsiteOpen(false); void sitesApi.list().then(setSites) }}
        />
      )}

      {aiCreateOpen && (
        <AiCreateModal
          onCreated={async (site) => {
            setAiCreateOpen(false)
            setSites(prev => [site, ...prev])
            initFromApiSite(site)
            navigate('/editor')
          }}
          onClose={() => setAiCreateOpen(false)}
        />
      )}

      {/* Create site modal */}
      {createOpen && (
        <CreateSiteModal
          onCreated={async (site) => {
            setCreateOpen(false)
            setSites(prev => [site, ...prev])
            initFromApiSite(site)
            navigate('/editor')
          }}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '380px', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700 }}>Удалить сайт?</h3>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#666' }}>Это действие нельзя отменить. Все страницы и данные сайта будут удалены.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Отмена</button>
              <button onClick={() => handleDeleteSite(deleteConfirm)} style={{ padding: '8px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Удалить</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename modal */}
      {editingOpen && (
        <RenameSiteModal
          site={sites.find(s => s.id === editingOpen)!}
          onRenamed={(updated) => {
            setSites(prev => prev.map(s => s.id === updated.id ? { ...s, name: updated.name, slug: updated.slug } : s))
            setEditingOpen(null)
          }}
          onClose={() => setEditingOpen(null)}
        />
      )}

      {/* Pricing modal */}
      {pricingOpen && (
        <PricingModal
          current={subscription?.effectivePlan ?? subscription?.plan ?? 'FREE'}
          onClose={() => setPricingOpen(false)}
        />
      )}

      {uploadSiteId && (
        <UploadHtmlModal
          siteId={uploadSiteId}
          onClose={() => setUploadSiteId(null)}
          onSuccess={() => {
            setSites(prev => prev.map(s => s.id === uploadSiteId ? { ...s, status: 'PUBLISHED' } : s))
            setUploadSiteId(null)
          }}
        />
      )}

      {/* Profile modal */}
      {profileOpen && profile && (
        <ProfileModal
          profile={profile}
          subscription={subscription}
          onUpdated={setProfile}
          onClose={() => setProfileOpen(false)}
        />
      )}

      {/* Create workspace modal */}
      {createWsOpen && (
        <CreateWorkspaceModal
          onCreated={(item) => {
            setWorkspaces(prev => [item, ...prev])
            setCreateWsOpen(false)
          }}
          onClose={() => setCreateWsOpen(false)}
        />
      )}

      {/* Manage workspace modal */}
      {manageWorkspace && (
        <WorkspaceManageModal
          item={manageWorkspace}
          onClose={() => setManageWorkspace(null)}
          onDeleted={() => {
            setWorkspaces(prev => prev.filter(w => w.workspace.id !== manageWorkspace.workspace.id))
            setManageWorkspace(null)
          }}
        />
      )}

      {/* Shop management modal */}
      {shopSite && (
        <SiteShopModal site={shopSite} onClose={() => setShopSite(null)} />
      )}

      {/* What's New modal */}
      {whatsNewOpen && <WhatsNewModal onClose={() => setWhatsNewOpen(false)} />}

      {/* NPS Survey */}
      {npsVisible && (
        <NpsSurvey
          profile={profile}
          onDismiss={() => setNpsVisible(false)}
        />
      )}
    </div>
  )
}

// ── Site Shop Modal ───────────────────────────────────────────────────────────

type ShopTab = 'products' | 'orders' | 'promos' | 'shipping'

function SiteShopModal({ site, onClose }: { site: ApiSite; onClose: () => void }) {
  const [tab, setTab] = useState<ShopTab>('products')
  const [products, setProducts] = useState<ApiProduct[]>([])
  const [orders, setOrders] = useState<ApiOrder[]>([])
  const [promos, setPromos] = useState<ApiPromoCode[]>([])
  const [zones, setZones] = useState<ApiShippingZone[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Product form
  const [showProductForm, setShowProductForm] = useState(false)
  const [editProduct, setEditProduct] = useState<ApiProduct | null>(null)
  const [pName, setPName] = useState('')
  const [pPrice, setPPrice] = useState('')
  const [pStock, setPStock] = useState('0')
  const [pDesc, setPDesc] = useState('')
  const [pPublished, setPPublished] = useState(false)
  const [pSaving, setPSaving] = useState(false)

  // Promo form
  const [showPromoForm, setShowPromoForm] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [promoType, setPromoType] = useState<'PERCENT' | 'FIXED'>('PERCENT')
  const [promoValue, setPromoValue] = useState('')
  const [promoSaving, setPromoSaving] = useState(false)

  // Shipping form
  const [showShippingForm, setShowShippingForm] = useState(false)
  const [zoneName, setZoneName] = useState('')
  const [zoneFlatRate, setZoneFlatRate] = useState('0')
  const [zoneSaving, setZoneSaving] = useState(false)

  useEffect(() => {
    load()
  }, [tab]) // eslint-disable-line

  async function load() {
    setLoading(true); setError('')
    try {
      if (tab === 'products') setProducts(await ecomApi.listProducts(site.id))
      if (tab === 'orders') setOrders(await ecomApi.listOrders(site.id))
      if (tab === 'promos') setPromos(await promoApi.list(site.id))
      if (tab === 'shipping') setZones(await shippingApi.list(site.id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }

  function openProductForm(p?: ApiProduct) {
    setEditProduct(p ?? null)
    setPName(p?.name ?? '')
    setPPrice(p ? String(parseFloat(p.price)) : '')
    setPStock(String(p?.stock ?? 0))
    setPDesc(p?.description ?? '')
    setPPublished(p?.published ?? false)
    setShowProductForm(true)
  }

  async function saveProduct(e: React.FormEvent) {
    e.preventDefault(); setPSaving(true)
    try {
      const price = parseFloat(pPrice)
      const baseData = { name: pName, stock: parseInt(pStock), description: pDesc || undefined, published: pPublished }
      if (editProduct) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updated = await ecomApi.updateProduct(site.id, editProduct.id, { ...baseData, price } as any)
        setProducts(prev => prev.map(p => p.id === updated.id ? updated : p))
      } else {
        const slug = pName.toLowerCase().replace(/[^a-zа-яё0-9]+/gi, '-').replace(/^-|-$/g, '') + '-' + Date.now()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const created = await ecomApi.createProduct(site.id, { ...baseData, price, slug } as any)
        setProducts(prev => [created, ...prev])
      }
      setShowProductForm(false)
    } catch (e) { setError(e instanceof Error ? e.message : 'Ошибка') }
    finally { setPSaving(false) }
  }

  async function deleteProduct(id: string) {
    if (!confirm('Удалить товар?')) return
    await ecomApi.deleteProduct(site.id, id)
    setProducts(prev => prev.filter(p => p.id !== id))
  }

  async function toggleOrderStatus(order: ApiOrder) {
    const next: Record<string, string> = { PENDING: 'CONFIRMED', CONFIRMED: 'PROCESSING', PROCESSING: 'SHIPPED', SHIPPED: 'DELIVERED' }
    const status = next[order.status] ?? order.status
    const updated = await ecomApi.updateOrderStatus(site.id, order.id, status)
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o))
  }

  async function savePromo(e: React.FormEvent) {
    e.preventDefault(); setPromoSaving(true)
    try {
      const created = await promoApi.create(site.id, { code: promoCode.toUpperCase(), type: promoType, value: parseFloat(promoValue) })
      setPromos(prev => [created, ...prev])
      setShowPromoForm(false); setPromoCode(''); setPromoValue('')
    } catch (e) { setError(e instanceof Error ? e.message : 'Ошибка') }
    finally { setPromoSaving(false) }
  }

  async function togglePromo(promo: ApiPromoCode) {
    const updated = await promoApi.update(site.id, promo.id, { active: !promo.active })
    setPromos(prev => prev.map(p => p.id === updated.id ? updated : p))
  }

  async function deletePromo(id: string) {
    if (!confirm('Удалить промокод?')) return
    await promoApi.delete(site.id, id)
    setPromos(prev => prev.filter(p => p.id !== id))
  }

  async function saveZone(e: React.FormEvent) {
    e.preventDefault(); setZoneSaving(true)
    try {
      const created = await shippingApi.create(site.id, { name: zoneName, flatRate: parseFloat(zoneFlatRate) || 0 })
      setZones(prev => [created, ...prev])
      setShowShippingForm(false); setZoneName(''); setZoneFlatRate('0')
    } catch (e) { setError(e instanceof Error ? e.message : 'Ошибка') }
    finally { setZoneSaving(false) }
  }

  async function deleteZone(id: string) {
    if (!confirm('Удалить зону доставки?')) return
    await shippingApi.delete(site.id, id)
    setZones(prev => prev.filter(z => z.id !== id))
  }

  const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modal: React.CSSProperties = { background: '#fff', borderRadius: 16, width: '90vw', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
  const hdr: React.CSSProperties = { padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
  const tabs: React.CSSProperties = { display: 'flex', gap: 4, padding: '12px 24px 0', borderBottom: '1px solid #eee', background: '#fafafa' }
  const body: React.CSSProperties = { flex: 1, overflow: 'auto', padding: 24 }
  const btn = (color = '#5b6af0'): React.CSSProperties => ({ padding: '8px 16px', background: color, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 })
  const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }
  const tbl: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #eee', color: '#666', fontWeight: 600, fontSize: 12 }
  const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13,
    fontWeight: active ? 700 : 400, color: active ? '#5b6af0' : '#666',
    borderBottom: active ? '2px solid #5b6af0' : '2px solid transparent', marginBottom: -1,
  })

  const statusColor: Record<string, string> = { PENDING: '#f59e0b', CONFIRMED: '#3b82f6', PROCESSING: '#8b5cf6', SHIPPED: '#06b6d4', DELIVERED: '#10b981', CANCELLED: '#ef4444', REFUNDED: '#6b7280' }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={hdr}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>🛒 Магазин — {site.name}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Товары, заказы, промокоды, доставка</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#999' }}>✕</button>
        </div>

        <div style={tabs}>
          {(['products', 'orders', 'promos', 'shipping'] as ShopTab[]).map(t => (
            <button key={t} style={tabStyle(tab === t)} onClick={() => setTab(t)}>
              {{ products: '📦 Товары', orders: '🧾 Заказы', promos: '🎟 Промокоды', shipping: '🚚 Доставка' }[t]}
            </button>
          ))}
        </div>

        <div style={body}>
          {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}
          {loading && <div style={{ color: '#888', textAlign: 'center', padding: 40 }}>Загрузка...</div>}

          {/* ── Products ── */}
          {!loading && tab === 'products' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button style={btn()} onClick={() => openProductForm()}>+ Добавить товар</button>
              </div>
              {showProductForm && (
                <form onSubmit={saveProduct} style={{ background: '#f9f9fb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Название</label><input style={inp} value={pName} onChange={e => setPName(e.target.value)} required /></div>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Цена (₽)</label><input style={inp} type="number" step="0.01" value={pPrice} onChange={e => setPPrice(e.target.value)} required /></div>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Остаток</label><input style={inp} type="number" value={pStock} onChange={e => setPStock(e.target.value)} /></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" id="pp" checked={pPublished} onChange={e => setPPublished(e.target.checked)} /><label htmlFor="pp" style={{ fontSize: 13 }}>Опубликован</label></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Описание</label><textarea style={{ ...inp, height: 60 }} value={pDesc} onChange={e => setPDesc(e.target.value)} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" style={btn()} disabled={pSaving}>{pSaving ? 'Сохранение...' : (editProduct ? 'Сохранить' : 'Создать')}</button>
                    <button type="button" style={btn('#888')} onClick={() => setShowProductForm(false)}>Отмена</button>
                  </div>
                </form>
              )}
              {products.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет товаров</div>
              ) : (
                <table style={tbl}>
                  <thead><tr><th style={th}>Название</th><th style={th}>Цена</th><th style={th}>Остаток</th><th style={th}>Статус</th><th style={th}></th></tr></thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id}>
                        <td style={td}><div style={{ fontWeight: 600 }}>{p.name}</div></td>
                        <td style={td}>{parseFloat(p.price).toLocaleString('ru-RU')} ₽</td>
                        <td style={td}>{p.stock}</td>
                        <td style={td}><span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, background: p.published ? '#d1fae5' : '#f3f4f6', color: p.published ? '#065f46' : '#6b7280' }}>{p.published ? 'Опубликован' : 'Черновик'}</span></td>
                        <td style={{ ...td, display: 'flex', gap: 8 }}>
                          <button style={btn('#f0f0f0')} onClick={() => openProductForm(p)} title="Редактировать"><span style={{ color: '#333' }}>✏️</span></button>
                          <button style={btn('#fee2e2')} onClick={() => deleteProduct(p.id)} title="Удалить"><span style={{ color: '#ef4444' }}>🗑</span></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* ── Orders ── */}
          {!loading && tab === 'orders' && (
            <>
              {orders.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет заказов</div>
              ) : (
                <table style={tbl}>
                  <thead><tr><th style={th}>#</th><th style={th}>Покупатель</th><th style={th}>Сумма</th><th style={th}>Статус</th><th style={th}>Дата</th><th style={th}></th></tr></thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id}>
                        <td style={td}><strong>#{o.number}</strong></td>
                        <td style={td}><div>{o.customerName ?? '—'}</div><div style={{ color: '#888', fontSize: 11 }}>{o.customerEmail}</div></td>
                        <td style={td}>{parseFloat(o.total).toLocaleString('ru-RU')} ₽</td>
                        <td style={td}><span style={{ padding: '3px 8px', borderRadius: 12, fontSize: 11, background: (statusColor[o.status] ?? '#888') + '20', color: statusColor[o.status] ?? '#888', fontWeight: 600 }}>{o.status}</span></td>
                        <td style={td}>{new Date(o.createdAt).toLocaleDateString('ru-RU')}</td>
                        <td style={td}>{!['DELIVERED','CANCELLED','REFUNDED'].includes(o.status) && <button style={btn('#5b6af0')} onClick={() => toggleOrderStatus(o)} title="Следующий статус">→</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* ── Promo Codes ── */}
          {!loading && tab === 'promos' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button style={btn()} onClick={() => setShowPromoForm(v => !v)}>+ Создать промокод</button>
              </div>
              {showPromoForm && (
                <form onSubmit={savePromo} style={{ background: '#f9f9fb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Код</label><input style={inp} value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="SUMMER20" required /></div>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Тип</label><select style={inp} value={promoType} onChange={e => setPromoType(e.target.value as 'PERCENT' | 'FIXED')}><option value="PERCENT">%</option><option value="FIXED">₽ фиксированно</option></select></div>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Значение</label><input style={inp} type="number" step="0.01" value={promoValue} onChange={e => setPromoValue(e.target.value)} required /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" style={btn()} disabled={promoSaving}>{promoSaving ? 'Создание...' : 'Создать'}</button>
                    <button type="button" style={btn('#888')} onClick={() => setShowPromoForm(false)}>Отмена</button>
                  </div>
                </form>
              )}
              {promos.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет промокодов</div>
              ) : (
                <table style={tbl}>
                  <thead><tr><th style={th}>Код</th><th style={th}>Скидка</th><th style={th}>Использований</th><th style={th}>Статус</th><th style={th}></th></tr></thead>
                  <tbody>
                    {promos.map(p => (
                      <tr key={p.id}>
                        <td style={td}><strong style={{ fontFamily: 'monospace' }}>{p.code}</strong></td>
                        <td style={td}>{p.type === 'PERCENT' ? `${p.value}%` : `${parseFloat(p.value).toLocaleString('ru-RU')} ₽`}</td>
                        <td style={td}>{p.usedCount}{p.usageLimit ? ` / ${p.usageLimit}` : ''}</td>
                        <td style={td}><span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, background: p.active ? '#d1fae5' : '#f3f4f6', color: p.active ? '#065f46' : '#6b7280' }}>{p.active ? 'Активен' : 'Отключён'}</span></td>
                        <td style={{ ...td, display: 'flex', gap: 8 }}>
                          <button style={btn(p.active ? '#888' : '#5b6af0')} onClick={() => togglePromo(p)}>{p.active ? 'Отключить' : 'Включить'}</button>
                          <button style={btn('#fee2e2')} onClick={() => deletePromo(p.id)} title="Удалить"><span style={{ color: '#ef4444' }}>🗑</span></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* ── Shipping ── */}
          {!loading && tab === 'shipping' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button style={btn()} onClick={() => setShowShippingForm(v => !v)}>+ Добавить зону</button>
              </div>
              {showShippingForm && (
                <form onSubmit={saveZone} style={{ background: '#f9f9fb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Название зоны</label><input style={inp} value={zoneName} onChange={e => setZoneName(e.target.value)} placeholder="Москва, Россия..." required /></div>
                    <div><label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Стоимость (₽)</label><input style={inp} type="number" step="0.01" value={zoneFlatRate} onChange={e => setZoneFlatRate(e.target.value)} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" style={btn()} disabled={zoneSaving}>{zoneSaving ? 'Создание...' : 'Создать'}</button>
                    <button type="button" style={btn('#888')} onClick={() => setShowShippingForm(false)}>Отмена</button>
                  </div>
                </form>
              )}
              {zones.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет зон доставки</div>
              ) : (
                <table style={tbl}>
                  <thead><tr><th style={th}>Название</th><th style={th}>Стоимость</th><th style={th}>Бесплатно от</th><th style={th}>Статус</th><th style={th}></th></tr></thead>
                  <tbody>
                    {zones.map(z => (
                      <tr key={z.id}>
                        <td style={td}><strong>{z.name}</strong></td>
                        <td style={td}>{parseFloat(z.flatRate).toLocaleString('ru-RU')} ₽</td>
                        <td style={td}>{z.freeAbove ? `${parseFloat(z.freeAbove).toLocaleString('ru-RU')} ₽` : '—'}</td>
                        <td style={td}><span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, background: z.active ? '#d1fae5' : '#f3f4f6', color: z.active ? '#065f46' : '#6b7280' }}>{z.active ? 'Активна' : 'Отключена'}</span></td>
                        <td style={td}><button style={btn('#fee2e2')} onClick={() => deleteZone(z.id)} title="Удалить"><span style={{ color: '#ef4444' }}>🗑</span></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Profile modal ─────────────────────────────────────────────────────────────

function UploadHtmlModal({ siteId, onClose, onSuccess }: { siteId: string; onClose: () => void; onSuccess: () => void }) {
  const [html, setHtml] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setHtml((ev.target?.result as string) ?? '')
    reader.readAsText(file)
  }

  const handleUpload = async () => {
    if (!html.trim()) { setError('Вставьте HTML или выберите файл'); return }
    setUploading(true)
    setError('')
    try {
      await sitesApi.uploadHtml(siteId, html)
      setDone(true)
      setTimeout(onSuccess, 1500)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '560px', maxWidth: '95vw', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>Загрузить HTML-сайт</h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#888' }}>Сайт будет опубликован сразу после загрузки</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#10b981' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
            <div style={{ fontWeight: 700, fontSize: '16px' }}>Сайт опубликован!</div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#444' }}>
                Выберите .html файл
              </label>
              <input
                type="file"
                accept=".html,.htm"
                onChange={handleFile}
                style={{ fontSize: '13px', color: '#333' }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#444' }}>
                или вставьте HTML-код
              </label>
              <textarea
                rows={10}
                placeholder="<!DOCTYPE html><html>...</html>"
                value={html}
                onChange={e => setHtml(e.target.value)}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#0369a1', marginBottom: '16px' }}>
              💡 HTML должен содержать все ресурсы инлайн или через CDN — локальные файлы <code>./style.css</code> не загрузятся.
            </div>
            {error && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '10px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Отмена
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !html.trim()}
                style={{ padding: '9px 20px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: uploading ? 'wait' : 'pointer', opacity: (!html.trim() || uploading) ? 0.6 : 1 }}
              >
                {uploading ? 'Загружаю...' : '⬆ Опубликовать'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function ProfileModal({ profile, subscription, onUpdated, onClose }: { profile: UserProfile; subscription?: SubscriptionInfo | null; onUpdated: (p: UserProfile) => void; onClose: () => void }) {
  const [name, setName] = useState(profile.name ?? '')
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')
  const [passMsg, setPassMsg] = useState('')
  const [passLoading, setPassLoading] = useState(false)

  async function handleSaveName(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true); setSaveMsg('')
    try {
      const updated = await api.patch<UserProfile>('/users/me', { name: name.trim() || null })
      onUpdated(updated)
      setSaveMsg('Сохранено!')
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Ошибка')
    } finally {
      setSaving(false)
      setTimeout(() => setSaveMsg(''), 3000)
    }
  }

  async function handleSendPasswordEmail() {
    setPassLoading(true); setPassMsg('')
    try {
      const res = await fetch(`${BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email }),
      })
      if (res.ok || res.status === 204) {
        setPassMsg('✓ Письмо отправлено! Проверьте почту.')
      } else {
        setPassMsg('Не удалось отправить письмо. Попробуйте позже.')
      }
    } catch {
      setPassMsg('Ошибка соединения. Попробуйте позже.')
    } finally {
      setPassLoading(false)
      setTimeout(() => setPassMsg(''), 6000)
    }
  }

  const planLabel: Record<string, string> = {
    FREE: 'Бесплатный', PERSONAL: 'Personal', BUSINESS: 'Business', AGENCY: 'Agency', ENTERPRISE: 'Enterprise'
  }

  const registeredAt = new Date(profile.createdAt).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: 480, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', padding: '32px', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Профиль</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer' }}>×</button>
        </div>

        {/* Avatar + Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: '16px', background: '#f9fafb', borderRadius: 10 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#5b6af0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 20, flexShrink: 0 }}>
            {profile.name ? profile.name[0].toUpperCase() : profile.email[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{profile.name ?? '—'}</div>
            <div style={{ fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile.email}</div>
            <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, background: '#f0f1ff', color: '#5b6af0', border: '1px solid #c7d2fe', padding: '2px 8px', borderRadius: 20 }}>
                {planLabel[subscription?.effectivePlan ?? profile.plan] ?? (subscription?.effectivePlan ?? profile.plan)}
              </span>
              {subscription?.isOnTrial && (
                <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: 20 }}>
                  Пробный период
                </span>
              )}
              <span style={{ fontSize: 11, color: '#9b9b9b' }}>с {registeredAt}</span>
            </div>
          </div>
        </div>

        {/* Info rows */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
          <div style={{ padding: '12px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Тариф</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#5b6af0' }}>
              {planLabel[subscription?.effectivePlan ?? profile.plan] ?? (subscription?.effectivePlan ?? profile.plan)}
              {subscription?.isOnTrial && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#d97706', padding: '1px 6px', borderRadius: 10 }}>trial</span>}
            </div>
          </div>
          <div style={{ padding: '12px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Регистрация</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#444' }}>{registeredAt}</div>
          </div>
        </div>

        {/* Update name */}
        <form onSubmit={handleSaveName} style={{ marginBottom: 28 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Имя</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ваше имя"
              style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
            />
            <button type="submit" disabled={saving} style={{ padding: '9px 16px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
              {saving ? '...' : 'Сохранить'}
            </button>
          </div>
          {saveMsg && <div style={{ fontSize: 12, color: saveMsg.includes('!') ? '#16a34a' : '#ef4444', marginTop: 6 }}>{saveMsg}</div>}
        </form>

        {/* Change password via email */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Изменить пароль</label>
          <div style={{ padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#666', marginBottom: 10, lineHeight: 1.5 }}>
            Для безопасности смена пароля выполняется через подтверждение на почте.<br />
            <span style={{ color: '#9b9b9b' }}>{profile.email}</span>
          </div>
          <button
            onClick={handleSendPasswordEmail}
            disabled={passLoading}
            style={{ width: '100%', padding: '10px 16px', background: '#111', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: passLoading ? 'not-allowed' : 'pointer', fontSize: 13, opacity: passLoading ? 0.7 : 1 }}
          >
            {passLoading ? 'Отправка...' : 'Отправить ссылку для смены пароля'}
          </button>
          {passMsg && (
            <div style={{ fontSize: 12, color: passMsg.startsWith('✓') ? '#16a34a' : '#ef4444', marginTop: 8, textAlign: 'center' }}>
              {passMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 40px' }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>✦</div>
      <h2 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 8px', color: '#111' }}>Создайте первый сайт</h2>
      <p style={{ fontSize: '15px', color: '#666', margin: '0 0 32px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
        Выберите шаблон или начните с чистого листа. Сайт опубликуется за секунду.
      </p>
      <button
        onClick={onNew}
        style={{ padding: '13px 28px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
      >
        + Создать сайт
      </button>
    </div>
  )
}

// ── Template thumbnail system ─────────────────────────────────────────────────

type ThumbCfg = { bg: string; nav: string; accent: string; surf: string; dark: boolean; lay: 'landing'|'portfolio'|'hero'|'grid'|'cards'|'article' }
const THUMBS: Record<string, ThumbCfg> = {
  '__blank__':    { bg:'#f9fafb', nav:'#f3f4f6', accent:'#9ca3af', surf:'#e5e7eb', dark:false, lay:'landing' },
  'landing':      { bg:'#eef2ff', nav:'#4338ca', accent:'#5b6af0', surf:'#c7d2fe', dark:false, lay:'landing' },
  'portfolio':    { bg:'#0f172a', nav:'#1e293b', accent:'#818cf8', surf:'#334155', dark:true,  lay:'portfolio' },
  'restaurant':   { bg:'#1c0a00', nav:'#0c0400', accent:'#f59e0b', surf:'#3b1f0a', dark:true,  lay:'hero' },
  'startup':      { bg:'#0a192f', nav:'#040e1e', accent:'#38bdf8', surf:'#172a45', dark:true,  lay:'landing' },
  'store':        { bg:'#fff7ed', nav:'#c2410c', accent:'#f97316', surf:'#fed7aa', dark:false, lay:'grid' },
  'blog':         { bg:'#fafafa', nav:'#111827', accent:'#4b5563', surf:'#f3f4f6', dark:false, lay:'article' },
  'fitness':      { bg:'#0c0c0c', nav:'#000',    accent:'#ef4444', surf:'#1c1c1c', dark:true,  lay:'hero' },
  'event':        { bg:'#1a0a2e', nav:'#0a0516', accent:'#f59e0b', surf:'#2d1852', dark:true,  lay:'cards' },
  'clinic':       { bg:'#f0fdf4', nav:'#14532d', accent:'#16a34a', surf:'#bbf7d0', dark:false, lay:'cards' },
  'real-estate':  { bg:'#1e293b', nav:'#0f172a', accent:'#f59e0b', surf:'#334155', dark:true,  lay:'grid' },
  'beauty':       { bg:'#fdf2f8', nav:'#831843', accent:'#ec4899', surf:'#fce7f3', dark:false, lay:'hero' },
  'education':    { bg:'#eff6ff', nav:'#1e40af', accent:'#3b82f6', surf:'#dbeafe', dark:false, lay:'cards' },
  'legal':        { bg:'#1a1a2e', nav:'#0a0a1e', accent:'#c7a53a', surf:'#2d2d52', dark:true,  lay:'landing' },
  'consulting':   { bg:'#f8fafc', nav:'#1e293b', accent:'#475569', surf:'#e2e8f0', dark:false, lay:'landing' },
  'it-agency':    { bg:'#050d1a', nav:'#020810', accent:'#00d4aa', surf:'#0d1f35', dark:true,  lay:'portfolio' },
  'travel':       { bg:'#f0f9ff', nav:'#0369a1', accent:'#0ea5e9', surf:'#bae6fd', dark:false, lay:'hero' },
  'accounting':   { bg:'#f8fafc', nav:'#1e293b', accent:'#3b82f6', surf:'#e2e8f0', dark:false, lay:'cards' },
  'construction': { bg:'#1c1917', nav:'#0c0a09', accent:'#f97316', surf:'#292524', dark:true,  lay:'hero' },
  'pet':          { bg:'#fff7ed', nav:'#7c3aed', accent:'#fb923c', surf:'#ffedd5', dark:false, lay:'cards' },
  'music':        { bg:'#09090b', nav:'#000',    accent:'#c026d3', surf:'#18181b', dark:true,  lay:'hero' },
  'wedding':      { bg:'#fdf2f8', nav:'#831843', accent:'#ec4899', surf:'#fce7f3', dark:false, lay:'hero' },
  'saas':         { bg:'#0f172a', nav:'#050a14', accent:'#6366f1', surf:'#1e293b', dark:true,  lay:'landing' },
  'yoga':         { bg:'#fefce8', nav:'#713f12', accent:'#ca8a04', surf:'#fef9c3', dark:false, lay:'cards' },
  'photography':  { bg:'#0a0a0a', nav:'#000',    accent:'#d1d5db', surf:'#1a1a1a', dark:true,  lay:'portfolio' },
  'nonprofit':    { bg:'#f0fdf4', nav:'#14532d', accent:'#16a34a', surf:'#dcfce7', dark:false, lay:'landing' },
  'food-delivery':{ bg:'#fff7ed', nav:'#9a3412', accent:'#ea580c', surf:'#ffedd5', dark:false, lay:'grid' },
  'barber':       { bg:'#111827', nav:'#030712', accent:'#fbbf24', surf:'#1f2937', dark:true,  lay:'hero' },
  'dentist':      { bg:'#f0f9ff', nav:'#075985', accent:'#0284c7', surf:'#bae6fd', dark:false, lay:'cards' },
  'logistics':    { bg:'#f8fafc', nav:'#1e3a5f', accent:'#1e40af', surf:'#e0e7ff', dark:false, lay:'landing' },
}

function TemplateThumbnail({ id, lg }: { id: string; lg?: boolean }) {
  const th = THUMBS[id] ?? THUMBS.landing
  const tc = th.dark ? 'rgba(255,255,255,' : 'rgba(0,0,0,'
  const title = tc + '0.65)'; const sub = tc + '0.28)'
  const H = lg ? 136 : 78; const navH = lg ? 13 : 9

  const Blk = ({ x, y, w, h, c, rx = 2 }: { x:number; y:number; w:number; h:number; c:string; rx?:number }) => (
    <div style={{ position:'absolute', left:`${x}%`, top:`${y}%`, width:`${w}%`, height:`${h}%`, background:c, borderRadius:rx }} />
  )
  const layouts: Record<string, ReactElement> = {
    landing: <><Blk x={4} y={8}  w={62} h={11} c={title} /><Blk x={4} y={23} w={46} h={7}  c={sub} /><Blk x={4} y={35} w={27} h={11} c={th.accent} rx={4} /><Blk x={2} y={56} w={30} h={38} c={th.surf} /><Blk x={35} y={56} w={30} h={38} c={th.surf} /><Blk x={68} y={56} w={30} h={38} c={th.surf} /></>,
    portfolio: <><Blk x={2}  y={4}  w={31} h={46} c={th.surf} /><Blk x={35} y={4}  w={31} h={46} c={th.surf} /><Blk x={68} y={4}  w={30} h={46} c={th.surf} /><Blk x={2}  y={54} w={48} h={40} c={th.surf} /><Blk x={52} y={54} w={46} h={40} c={th.surf} /></>,
    hero: <><Blk x={2} y={4} w={96} h={62} c={th.surf} rx={4} /><Blk x={15} y={11} w={70} h={13} c={th.accent} rx={3} /><Blk x={20} y={29} w={60} h={7} c={sub} rx={2} /><Blk x={32} y={42} w={36} h={14} c={th.accent} rx={4} /><Blk x={2} y={72} w={30} h={24} c={th.surf} /><Blk x={35} y={72} w={30} h={24} c={th.surf} /><Blk x={68} y={72} w={30} h={24} c={th.surf} /></>,
    grid: <><Blk x={2}  y={4}  w={30} h={44} c={th.surf} /><Blk x={35} y={4}  w={30} h={44} c={th.surf} /><Blk x={68} y={4}  w={30} h={44} c={th.surf} /><Blk x={2}  y={52} w={22} h={9}  c={th.accent} rx={3} /><Blk x={35} y={52} w={22} h={9}  c={th.accent} rx={3} /><Blk x={68} y={52} w={22} h={9}  c={th.accent} rx={3} /><Blk x={2}  y={65} w={30} h={30} c={th.surf} /><Blk x={35} y={65} w={30} h={30} c={th.surf} /><Blk x={68} y={65} w={30} h={30} c={th.surf} /></>,
    cards: <><Blk x={4} y={7}  w={60} h={12} c={title} /><Blk x={4} y={24} w={28} h={10} c={th.accent} rx={4} /><Blk x={2} y={40} w={46} h={28} c={th.surf} /><Blk x={52} y={40} w={46} h={28} c={th.surf} /><Blk x={2} y={72} w={46} h={24} c={th.surf} /><Blk x={52} y={72} w={46} h={24} c={th.surf} /></>,
    article: <><Blk x={2} y={4} w={96} h={40} c={th.surf} rx={3} /><Blk x={2} y={49} w={30} h={30} c={th.surf} rx={2} /><Blk x={35} y={49} w={30} h={30} c={th.surf} rx={2} /><Blk x={68} y={49} w={30} h={30} c={th.surf} rx={2} /><Blk x={4} y={83} w={44} h={6} c={sub} /><Blk x={4} y={93} w={30} h={5} c={sub} /></>,
  }

  return (
    <div style={{ height:H, background:th.bg, borderRadius:6, overflow:'hidden', display:'flex', flexDirection:'column', flexShrink:0 }}>
      <div style={{ height:navH, background:th.nav, display:'flex', alignItems:'center', gap:4, padding:'0 6px', flexShrink:0 }}>
        <div style={{ width:18, height:3, background:th.accent, borderRadius:1, flexShrink:0 }} />
        <div style={{ flex:1 }} />
        <div style={{ width:12, height:2, background:th.dark ? 'rgba(255,255,255,.3)':'rgba(255,255,255,.5)', borderRadius:1 }} />
        <div style={{ width:12, height:2, background:th.dark ? 'rgba(255,255,255,.3)':'rgba(255,255,255,.5)', borderRadius:1 }} />
        <div style={{ width:20, height:6, background:th.accent, borderRadius:2 }} />
      </div>
      <div style={{ flex:1, position:'relative', overflow:'hidden' }}>
        {layouts[th.lay] ?? layouts.landing}
      </div>
    </div>
  )
}

// ── Site card ─────────────────────────────────────────────────────────────────

interface SiteCardProps {
  site: ApiSite
  userPlan?: string
  onEdit: () => void
  onUpload?: () => void
  onDelete: () => void
  onRename: () => void
  onClone: () => Promise<void>
  onShop: () => void
  onPublish: () => Promise<void>
  onUnpublish: () => Promise<void>
  onGalleryToggle: () => Promise<void>
  onDashboard?: () => void
  onPublishAsTemplate?: () => Promise<void>
}

async function downloadSiteZip(siteId: string, siteName: string) {
  const res = await fetch(`${BASE}/sites/${siteId}/export`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('bst-token') ?? ''}` },
  })
  if (!res.ok) throw new Error(`Export failed: ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${siteName}.zip`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

async function downloadSiteJson(siteId: string, siteName: string) {
  const res = await fetch(`${BASE}/sites/${siteId}/export-json`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('bst-token') ?? ''}` },
  })
  if (!res.ok) throw new Error(`Export failed: ${res.status}`)
  const data = await res.json()
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${siteName}-backup.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function SiteCard({ site, userPlan, onEdit, onUpload, onDelete, onRename, onClone, onShop, onPublish, onUnpublish, onGalleryToggle, onDashboard, onPublishAsTemplate }: SiteCardProps) {
  const isHostingPlan = userPlan === 'HOSTING'
  const [menuOpen, setMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportingJson, setExportingJson] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishDone, setPublishDone] = useState(false)
  const [togglingGallery, setTogglingGallery] = useState(false)
  const isPublished = site.status === 'PUBLISHED'
  const pageCount = site.pages?.length ?? 0

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        cursor: 'default',
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
    >
      {/* Preview area */}
      <div
        style={{ height: '140px', background: 'linear-gradient(135deg,#f0f4ff,#e8ecff)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
        onClick={onEdit}
      >
        <div style={{ fontSize: '40px', fontWeight: 900, color: '#c7ceff', letterSpacing: '-2px' }}>
          {site.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
        >
          <div style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}>
            Редактировать
          </div>
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
          <div>
            <h3 style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: 700, color: '#111', lineHeight: 1.3 }}>{site.name}</h3>
            <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {site.customDomainVerified && site.customDomain
                ? <><span style={{ color: '#10b981', fontWeight: 600 }}>{site.customDomain}</span><span style={{ background: '#dcfce7', color: '#16a34a', padding: '0 5px', borderRadius: '4px', fontWeight: 700, fontSize: '10px' }}>SSL</span></>
                : <>{site.slug}.mybst.ru</>
              }
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o) }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#888', padding: '4px', lineHeight: 1 }}
            >
              ⋮
            </button>
            {menuOpen && (
              <div
                style={{ position: 'absolute', right: 0, top: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '200px', zIndex: 100 }}
                onClick={e => e.stopPropagation()}
              >
                {[
                  { label: '✏ Редактировать', action: () => { setMenuOpen(false); onEdit() } },
                  { label: '📊 Dashboard', action: () => { setMenuOpen(false); onDashboard?.() } },
                  { label: '✎ Переименовать', action: () => { setMenuOpen(false); onRename() } },
                  {
                    label: publishing ? '⏳...' : (isPublished || publishDone) ? '✕ Снять с публикации' : '🚀 Опубликовать',
                    action: async () => {
                      setMenuOpen(false)
                      setPublishing(true)
                      try {
                        if (isPublished || publishDone) {
                          await onUnpublish()
                        } else {
                          await onPublish()
                          setPublishDone(true)
                          setTimeout(() => setPublishDone(false), 3000)
                        }
                      } catch (e) {
                        alert(e instanceof Error && e.message ? e.message : 'Не удалось выполнить операцию. Попробуйте ещё раз.')
                      } finally { setPublishing(false) }
                    },
                    disabled: publishing,
                  },
                  { label: '🛒 Магазин', action: () => { setMenuOpen(false); onShop() } },
                  { label: '🌐 Открыть сайт', action: () => { setMenuOpen(false); window.open(site.customDomainVerified && site.customDomain ? `https://${site.customDomain}` : `https://${site.slug}.mybst.ru`, '_blank') }, disabled: !isPublished && !publishDone },
                  {
                    label: cloning ? '⏳ Копирование...' : '⊕ Дублировать',
                    action: async () => {
                      setMenuOpen(false)
                      setCloning(true)
                      try { await onClone() } catch { /* handled in parent */ } finally { setCloning(false) }
                    },
                    disabled: cloning,
                  },
                  {
                    label: exporting ? '⏳ Экспорт...' : '⬇ Скачать HTML',
                    action: async () => {
                      setMenuOpen(false)
                      setExporting(true)
                      try { await downloadSiteZip(site.id, site.slug) } catch { alert('Ошибка экспорта') } finally { setExporting(false) }
                    },
                    disabled: exporting,
                  },
                  {
                    label: exportingJson ? '⏳ JSON...' : '⬇ Backup JSON',
                    action: async () => {
                      setMenuOpen(false)
                      setExportingJson(true)
                      try { await downloadSiteJson(site.id, site.slug) } catch { alert('Ошибка экспорта') } finally { setExportingJson(false) }
                    },
                    disabled: exportingJson,
                  },
                  {
                    label: togglingGallery ? '⏳...' : site.publiclyListed ? '🌍 В галерее ✓' : '🌍 Добавить в галерею',
                    action: async () => {
                      setMenuOpen(false)
                      setTogglingGallery(true)
                      try { await onGalleryToggle() } catch { /* noop */ } finally { setTogglingGallery(false) }
                    },
                    disabled: togglingGallery || !isPublished,
                  },
                  {
                    label: '📋 Опубликовать как шаблон',
                    action: async () => {
                      setMenuOpen(false)
                      await onPublishAsTemplate?.()
                    },
                    disabled: !isPublished && !publishDone,
                  },
                  { label: '🗑 Удалить черновик', action: () => { setMenuOpen(false); onDelete() }, danger: true },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    disabled={item.disabled}
                    style={{
                      display: 'block', width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: item.disabled ? 'not-allowed' : 'pointer',
                      textAlign: 'left', fontSize: '13px', color: item.danger ? '#ef4444' : item.disabled ? '#ccc' : '#333',
                      fontWeight: 500, whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = '#f3f4f6' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', color: '#888' }}>
          <span>{pageCount} стр.</span>
          <span
            style={{
              padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
              background: (isPublished || publishDone) ? '#dcfce7' : '#f3f4f6',
              color: (isPublished || publishDone) ? '#16a34a' : '#888',
            }}
          >
            {(isPublished || publishDone) ? '● Опубликован' : '○ Черновик'}
          </span>
          {site.updatedAt && (
            <span style={{ marginLeft: 'auto', fontSize: '11px' }}>
              {new Date(site.updatedAt).toLocaleDateString('ru', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
          {isHostingPlan ? (
            <button
              onClick={onUpload}
              style={{ flex: 1, padding: '7px', background: '#e0f2fe', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}
            >
              ⬆ Загрузить HTML
            </button>
          ) : (
            <button
              onClick={onEdit}
              style={{ flex: 1, padding: '7px', background: '#f3f4f6', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, color: '#333', cursor: 'pointer' }}
            >
              ✏ Редактировать
            </button>
          )}
          {(isPublished || publishDone) ? (
            <button
              onClick={async () => {
                if (publishing) return
                setPublishing(true)
                try { await onUnpublish() } catch { /* noop */ } finally { setPublishing(false) }
              }}
              style={{ flex: 1, padding: '7px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '12px', fontWeight: 600, color: '#6b7280', cursor: 'pointer', opacity: publishing ? 0.7 : 1 }}
            >
              {publishing ? '...' : '✕ Снять'}
            </button>
          ) : (
            <button
              onClick={async () => {
                if (publishing) return
                setPublishing(true)
                try {
                  await onPublish()
                  setPublishDone(true)
                  setTimeout(() => setPublishDone(false), 3000)
                } catch { /* noop */ } finally { setPublishing(false) }
              }}
              style={{ flex: 1, padding: '7px', background: '#5b6af0', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: 600, color: '#fff', cursor: 'pointer', opacity: publishing ? 0.7 : 1 }}
            >
              {publishing ? '...' : '🚀 Опубликовать'}
            </button>
          )}
          <button
            onClick={onDelete}
            title="Удалить сайт"
            style={{ padding: '7px 10px', background: '#fee2e2', border: 'none', borderRadius: '7px', fontSize: '13px', color: '#dc2626', cursor: 'pointer' }}
          >
            🗑
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Create site modal ─────────────────────────────────────────────────────────

interface CreateSiteModalProps {
  onCreated: (site: ApiSite) => void
  onClose: () => void
}

function CreateSiteModal({ onCreated, onClose }: CreateSiteModalProps) {
  const [step, setStep] = useState<'mode' | 'template' | 'name' | 'code' | 'figma'>('mode')
  const [aiMode, setAiMode] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null)
  const [selectedColorVariant, setSelectedColorVariant] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [htmlCode, setHtmlCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initFromApiSite = useEditorStore(s => s.initFromApiSite)

  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'my-site'

  async function handleCreateFromCode() {
    if (!name.trim()) { setError('Введите название сайта'); return }
    if (!htmlCode.trim()) { setError('Вставьте HTML код'); return }
    setLoading(true)
    setError(null)
    try {
      const uniqueSlug = `${slug}-${Date.now().toString(36)}`
      const site = await sitesApi.create(name.trim(), uniqueSlug)
      await sitesApi.uploadHtml(site.id, htmlCode)
      const updatedSite = await sitesApi.get(site.id).catch(() => site)
      initFromApiSite(updatedSite)
      onCreated(updatedSite)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка создания')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate() {
    if (!name.trim()) { setError('Введите название сайта'); return }
    setLoading(true)
    setError(null)
    try {
      const uniqueSlug = `${slug}-${Date.now().toString(36)}`
      const site = await sitesApi.create(name.trim(), uniqueSlug)
      // Apply template if selected
      const template = SITE_TEMPLATES.find(t => t.id === selectedTemplate)
      if (template) {
        // Save templateId so StyleSwitcherModal knows which variants to show
        localStorage.setItem(`bst-template-${site.id}`, template.id)
        const variant = selectedVariant ? template.variants?.find(v => v.id === selectedVariant) : null
        const colorVariant = selectedColorVariant ? variant?.colorVariants?.find(cv => cv.id === selectedColorVariant) : null
        const pagesToUse = colorVariant?.pages ?? variant?.pages ?? template.pages
        const siteName = name.trim()
        // Load the template pages via upsert
        for (const page of pagesToUse) {
          // Deep-clone sections and replace demo brand names in navbar with actual site name
          const sections = JSON.parse(JSON.stringify(page.sections)) as typeof page.sections
          for (const section of sections) {
            for (const block of section.blocks) {
              if (block.type === 'navbar' || block.type === 'footer') {
                if (block.data.logo) block.data.logo = siteName
                if (block.data.brand) block.data.brand = siteName
              }
              if (block.type.startsWith('swiss-')) {
                block.data.brand = siteName
              }
            }
          }
          await sitesApi.upsertPage(site.id, {
            title: page.title,
            slug: page.slug,
            isHome: page.isHome,
            content: { sections },
          })
        }
        // Re-fetch site to get the updated pages
        const updatedSite = await sitesApi.get(site.id).catch(() => site)
        initFromApiSite(updatedSite)
        onCreated(updatedSite)
      } else {
        initFromApiSite(site)
        if (aiMode) localStorage.setItem('bst-open-ai', '1')
        onCreated(site)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка создания')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: '16px', width: step === 'template' ? '980px' : '720px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', transition: 'width 0.2s' }}>
        {/* Modal header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>Создать новый сайт</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
              {step === 'mode' && 'Выберите способ создания'}
              {step === 'template' && 'Шаг 2 из 3: Выберите шаблон'}
              {step === 'name' && !aiMode && 'Шаг 3 из 3: Название сайта'}
              {step === 'name' && aiMode && 'Шаг 2 из 2: Название сайта'}
              {step === 'code' && 'Шаг 2 из 2: HTML код сайта'}
              {step === 'figma' && 'Шаг 2 из 2: Figma импорт'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#888', cursor: 'pointer' }}>×</button>
        </div>

        {/* Modal body */}
        <div style={{ overflow: 'auto', flex: 1 }}>
          {step === 'mode' && (
            <div style={{ padding: '28px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '16px' }}>
                {/* AI card — highlight at top-left as primary option */}
                <div
                  onClick={() => { setAiMode(true); setStep('name') }}
                  style={{ border: '2px solid #5b6af0', borderRadius: '14px', padding: '24px 20px', cursor: 'pointer', textAlign: 'center', background: 'linear-gradient(135deg,#f5f6ff,#fdf4ff)', transition: 'all 0.15s', position: 'relative' }}
                  onMouseEnter={e => { const d = e.currentTarget; d.style.borderColor = '#7c3aed'; d.style.background = 'linear-gradient(135deg,#eef0ff,#fae8ff)' }}
                  onMouseLeave={e => { const d = e.currentTarget; d.style.borderColor = '#5b6af0'; d.style.background = 'linear-gradient(135deg,#f5f6ff,#fdf4ff)' }}
                >
                  <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'linear-gradient(135deg,#5b6af0,#7c3aed)', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '4px' }}>Новинка</div>
                  <div style={{ fontSize: '44px', marginBottom: '14px' }}>✨</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>Сайт с помощью AI</div>
                  <div style={{ fontSize: '13px', color: '#555', lineHeight: 1.5 }}>Опишите ваш бизнес, и AI создаст полноценный сайт за пару минут</div>
                  <div style={{ marginTop: '18px', display: 'inline-block', background: 'linear-gradient(135deg,#5b6af0,#7c3aed)', color: '#fff', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}>
                    Создать →
                  </div>
                </div>

                <div
                  onClick={() => { setAiMode(false); setStep('template') }}
                  style={{ border: '2px solid #e5e7eb', borderRadius: '14px', padding: '24px 20px', cursor: 'pointer', textAlign: 'center', background: '#fff', transition: 'all 0.15s' }}
                  onMouseEnter={e => { const d = e.currentTarget; d.style.borderColor = '#5b6af0'; d.style.background = '#f5f6ff' }}
                  onMouseLeave={e => { const d = e.currentTarget; d.style.borderColor = '#e5e7eb'; d.style.background = '#fff' }}
                >
                  <div style={{ fontSize: '44px', marginBottom: '14px' }}>🏗</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>Конструктор BST</div>
                  <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>Выберите готовый шаблон и настройте его в визуальном редакторе</div>
                  <div style={{ marginTop: '18px', display: 'inline-block', background: '#5b6af0', color: '#fff', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}>
                    Выбрать →
                  </div>
                </div>

                <div
                  onClick={() => { setAiMode(false); setStep('code') }}
                  style={{ border: '2px solid #e5e7eb', borderRadius: '14px', padding: '24px 20px', cursor: 'pointer', textAlign: 'center', background: '#fff', transition: 'all 0.15s' }}
                  onMouseEnter={e => { const d = e.currentTarget; d.style.borderColor = '#0ea5e9'; d.style.background = '#f0fbff' }}
                  onMouseLeave={e => { const d = e.currentTarget; d.style.borderColor = '#e5e7eb'; d.style.background = '#fff' }}
                >
                  <div style={{ fontSize: '36px', fontWeight: 900, fontFamily: 'monospace', color: '#0ea5e9', marginBottom: '14px', lineHeight: 1 }}>{'</>'}</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>Вставить код</div>
                  <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>Загрузите готовый HTML-код вашего сайта и опубликуйте его мгновенно</div>
                  <div style={{ marginTop: '18px', display: 'inline-block', background: '#0ea5e9', color: '#fff', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}>
                    Загрузить →
                  </div>
                </div>

                <div
                  onClick={() => { setAiMode(false); setStep('figma') }}
                  style={{ border: '2px solid #e5e7eb', borderRadius: '14px', padding: '24px 20px', cursor: 'pointer', textAlign: 'center', background: '#fff', transition: 'all 0.15s', position: 'relative' }}
                  onMouseEnter={e => { const d = e.currentTarget; d.style.borderColor = '#8b5cf6'; d.style.background = '#faf5ff' }}
                  onMouseLeave={e => { const d = e.currentTarget; d.style.borderColor = '#e5e7eb'; d.style.background = '#fff' }}
                >
                  <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#f59e0b', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '4px' }}>Скоро</div>
                  <div style={{ fontSize: '44px', marginBottom: '14px' }}>🎨</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '8px' }}>Figma дизайн</div>
                  <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>Импортируйте дизайн-макет из Figma и конвертируйте в готовый сайт</div>
                  <div style={{ marginTop: '18px', display: 'inline-block', background: '#8b5cf6', color: '#fff', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 700 }}>
                    Подключить →
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'template' && (
            <div style={{ display: 'flex' }}>
              {/* Template grid */}
              <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '12px' }}>
                  <TemplateCard
                    id="__blank__"
                    label="Пустой сайт"
                    description="Начните с нуля"
                    icon="✦"
                    pageCount={1}
                    selected={selectedTemplate === '__blank__' || selectedTemplate === null}
                    onSelect={() => setSelectedTemplate('__blank__')}
                  />
                  {SITE_TEMPLATES.map(t => (
                    <TemplateCard
                      key={t.id}
                      id={t.id}
                      label={t.label}
                      description={t.description}
                      icon={t.icon}
                      pageCount={t.pages.length}
                      selected={selectedTemplate === t.id}
                      onSelect={() => { setSelectedTemplate(t.id); setSelectedVariant(t.variants?.length === 1 ? t.variants[0].id : null); setSelectedColorVariant(null) }}
                      hasVariants={!!(t.variants && t.variants.length > 0)}
                    />
                  ))}
                </div>
              </div>
              {/* Preview panel */}
              {(() => {
                const tpl = SITE_TEMPLATES.find(t => t.id === selectedTemplate)
                if (!tpl) return (
                  <div style={{ width: '300px', borderLeft: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '13px', gap: '8px', position: 'sticky', top: 0, alignSelf: 'flex-start', minHeight: '200px', padding: '40px 24px' }}>
                    <div style={{ fontSize: '32px' }}>👈</div>
                    <div>Выберите шаблон<br/>для предпросмотра</div>
                  </div>
                )
                const activeVariant = selectedVariant ? tpl.variants?.find(v => v.id === selectedVariant) : null
                const activePages = activeVariant?.pages ?? tpl.pages
                const totalBlocks = activePages.reduce((sum, p) => sum + p.sections.reduce((s, sec) => s + sec.blocks.length, 0), 0)
                return (
                  <div style={{ width: '300px', borderLeft: '1px solid #e5e7eb', overflowY: 'auto', flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start', maxHeight: 'calc(90vh - 130px)' }}>
                    <div key={tpl.id} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', animation: 'bst-slide-in-right 0.2s ease' }}>
                      <div>
                        <TemplateThumbnail id={tpl.id} lg />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                          <span style={{ fontSize: '24px', lineHeight: 1, flexShrink: 0 }}>{tpl.icon}</span>
                          <div>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: '#111' }}>{activeVariant ? `${tpl.label} · ${activeVariant.label}` : tpl.label}</div>
                            <div style={{ fontSize: '12px', color: '#666', lineHeight: 1.5 }}>{activeVariant?.description ?? tpl.description}</div>
                          </div>
                        </div>
                      </div>

                      {/* Structural style variants — only show picker when multiple options */}
                      {tpl.variants && tpl.variants.length > 1 && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                            Визуальный стиль
                          </div>
                          <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px' }}>
                            Выберите дизайн-концепцию шаблона
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            <button
                              onClick={() => { setSelectedVariant(null); setSelectedColorVariant(null) }}
                              style={{ padding: '4px 9px', borderRadius: '6px', border: `1.5px solid ${!selectedVariant ? '#5b6af0' : '#e5e7eb'}`, background: !selectedVariant ? '#5b6af0' : '#fff', color: !selectedVariant ? '#fff' : '#555', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                            >
                              По умолч.
                            </button>
                            {tpl.variants.map(v => (
                              <button
                                key={v.id}
                                onClick={() => { setSelectedVariant(v.id); setSelectedColorVariant(null) }}
                                style={{ padding: '4px 9px', borderRadius: '6px', border: `1.5px solid ${selectedVariant === v.id ? '#5b6af0' : '#e5e7eb'}`, background: selectedVariant === v.id ? '#5b6af0' : '#fff', color: selectedVariant === v.id ? '#fff' : '#555', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                {v.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Color sub-variants (e.g. Swiss: Classic / Warm / Earth / Dark) */}
                      {(() => {
                        const activeVar = selectedVariant ? tpl.variants?.find(v => v.id === selectedVariant) : null
                        if (!activeVar?.colorVariants?.length) return null
                        return (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                              Цветовой вариант
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                              {activeVar.colorVariants.map(cv => (
                                <button
                                  key={cv.id}
                                  onClick={() => setSelectedColorVariant(cv.id)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '7px 10px', borderRadius: '8px',
                                    border: `1.5px solid ${selectedColorVariant === cv.id ? '#5b6af0' : '#e5e7eb'}`,
                                    background: selectedColorVariant === cv.id ? '#f0f1ff' : '#fff',
                                    cursor: 'pointer', textAlign: 'left', width: '100%',
                                  }}
                                >
                                  <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: cv.bg, border: '1px solid rgba(0,0,0,0.1)' }} />
                                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: cv.accent }} />
                                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: cv.ink, border: '1px solid rgba(0,0,0,0.08)' }} />
                                  </div>
                                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>{cv.label}</span>
                                  {selectedColorVariant === cv.id && <span style={{ marginLeft: 'auto', color: '#5b6af0', fontSize: '12px' }}>✓</span>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      })()}

                      <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Страницы ({activePages.length})
                        </div>
                        {activePages.map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px' }}>{p.isHome ? '🏠' : '📄'}</span>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{p.title}</div>
                              <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>{p.slug}</div>
                            </div>
                            <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>
                              {p.sections.reduce((s, sec) => s + sec.blocks.length, 0)} бл.
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1, background: '#f0f4ff', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: '#5b6af0' }}>{activePages.length}</div>
                          <div style={{ fontSize: '11px', color: '#888' }}>страниц</div>
                        </div>
                        <div style={{ flex: 1, background: '#f0fdf4', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: '#16a34a' }}>{totalBlocks}</div>
                          <div style={{ fontSize: '11px', color: '#888' }}>блоков</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {step === 'name' && (
            <div style={{ padding: '32px 24px' }}>
              <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#444' }}>
                  Название сайта
                </label>
                <input
                  autoFocus
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
                  placeholder="Мой новый сайт"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box', outline: 'none' }}
                />
                <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                  Адрес: <span style={{ fontFamily: 'monospace' }}>{slug}.mybst.ru</span>
                </div>
                {error && <div style={{ marginTop: '8px', fontSize: '13px', color: '#ef4444' }}>{error}</div>}
              </div>
            </div>
          )}

          {step === 'code' && (
            <div style={{ padding: '24px' }}>
              <div style={{ maxWidth: '580px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#444' }}>
                    Название сайта
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Мой сайт"
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box', outline: 'none' }}
                  />
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                    Адрес: <span style={{ fontFamily: 'monospace' }}>{slug}.mybst.ru</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#444' }}>
                    HTML код <span style={{ fontWeight: 400, color: '#888' }}>(вставьте полный HTML)</span>
                  </label>
                  <textarea
                    value={htmlCode}
                    onChange={e => setHtmlCode(e.target.value)}
                    placeholder={'<!DOCTYPE html>\n<html>\n<head>...</head>\n<body>...</body>\n</html>'}
                    rows={13}
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none', resize: 'vertical', lineHeight: 1.6 }}
                  />
                </div>
                {error && <div style={{ fontSize: '13px', color: '#ef4444' }}>{error}</div>}
              </div>
            </div>
          )}

          {step === 'figma' && (
            <div style={{ padding: '40px 24px' }}>
              <div style={{ maxWidth: '440px', margin: '0 auto', textAlign: 'center' }}>
                <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎨</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#111', marginBottom: '10px' }}>Figma импорт</div>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: 1.7, marginBottom: '24px' }}>
                  Мы работаем над интеграцией с Figma — она позволит автоматически конвертировать дизайн-макеты в готовые сайты BST.
                </div>
                <div style={{ background: '#fef9ec', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'left' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#92400e', marginBottom: '6px' }}>Скоро будет доступно</div>
                  <div style={{ fontSize: '13px', color: '#78350f', lineHeight: 1.6 }}>
                    Figma Plugin для экспорта дизайна прямо в BST. Оставьте email — мы пришлём уведомление первыми.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                  />
                  <button style={{ padding: '10px 18px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                    Сообщить
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
          {step === 'mode' && (
            <button onClick={onClose} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              Отмена
            </button>
          )}
          {step === 'template' && (
            <>
              <button onClick={() => setStep('mode')} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                ← Назад
              </button>
              <button onClick={() => setStep('name')} style={{ padding: '9px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>
                Далее →
              </button>
            </>
          )}
          {step === 'name' && (
            <>
              <button onClick={() => setStep(aiMode ? 'mode' : 'template')} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                ← Назад
              </button>
              <button
                onClick={handleCreate}
                disabled={loading || !name.trim()}
                style={{ padding: '9px 20px', background: loading ? '#a5b4fc' : aiMode ? 'linear-gradient(135deg,#5b6af0,#7c3aed)' : '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Создание...' : aiMode ? '✨ Создать и открыть AI' : 'Создать сайт'}
              </button>
            </>
          )}
          {step === 'code' && (
            <>
              <button onClick={() => setStep('mode')} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                ← Назад
              </button>
              <button
                onClick={handleCreateFromCode}
                disabled={loading || !name.trim() || !htmlCode.trim()}
                style={{ padding: '9px 20px', background: loading ? '#7dd3fc' : '#0ea5e9', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: (loading || !name.trim() || !htmlCode.trim()) ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Загрузка...' : 'Создать сайт'}
              </button>
            </>
          )}
          {step === 'figma' && (
            <>
              <button onClick={() => setStep('mode')} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                ← Назад
              </button>
              <button onClick={onClose} style={{ padding: '9px 20px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>
                Закрыть
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function TemplateCard({ id, label, description, icon, pageCount, selected, onSelect, hasVariants }: {
  id: string; label: string; description: string; icon: string; pageCount?: number; selected: boolean; onSelect: () => void; hasVariants?: boolean
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        border: `2px solid ${selected ? '#5b6af0' : '#e5e7eb'}`,
        borderRadius: '10px',
        padding: '8px',
        cursor: 'pointer',
        background: selected ? '#f0f4ff' : '#fff',
        transition: 'all 0.15s',
        position: 'relative',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = '#a5b4fc' }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = '#e5e7eb' }}
    >
      <TemplateThumbnail id={id} />
      {pageCount && pageCount > 1 && (
        <div style={{ position: 'absolute', top: '14px', right: '12px', background: '#5b6af0', color: '#fff', fontSize: '10px', fontWeight: 700, borderRadius: '4px', padding: '2px 5px', zIndex: 1 }}>
          {pageCount} стр.
        </div>
      )}
      {hasVariants && (
        <div style={{ position: 'absolute', top: pageCount && pageCount > 1 ? '34px' : '14px', right: '12px', background: '#f59e0b', color: '#fff', fontSize: '9px', fontWeight: 700, borderRadius: '4px', padding: '2px 5px', zIndex: 1 }}>
          9 стилей
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', paddingLeft: '2px' }}>
        <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
          <div style={{ fontSize: '10px', color: '#888', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{description}</div>
        </div>
      </div>
    </div>
  )
}

// ── Rename site modal ─────────────────────────────────────────────────────────

interface RenameSiteModalProps {
  site: ApiSite
  onRenamed: (site: ApiSite) => void
  onClose: () => void
}

// ── AI create site modal ──────────────────────────────────────────────────────

interface AiCreateModalProps {
  onCreated: (site: ApiSite) => void
  onClose: () => void
}

interface AiGeneratedPage {
  title: string
  seoTitle?: string
  seoDesc?: string
  sections: Array<{
    id: string
    blocks: Array<{ id: string; type: string; data: Record<string, unknown> }>
  }>
}

const AI_EXAMPLE_PROMPTS = [
  'Лендинг для студии йоги с расписанием, ценами и отзывами',
  'Сайт IT-агентства с портфолио, командой и формой заявки',
  'Магазин крафтового мыла ручной работы с описанием и доставкой',
  'Landing page для мобильного приложения по медитации',
  'Сайт ресторана японской кухни с меню и бронированием',
]

function AiCreateModal({ onCreated, onClose }: AiCreateModalProps) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<AiGeneratedPage | null>(null)
  const initFromApiSite = useEditorStore(s => s.initFromApiSite)

  async function handleGenerate() {
    const trimmed = prompt.trim()
    if (trimmed.length < 10) { setError('Опишите сайт подробнее (минимум 10 символов)'); return }
    setLoading(true)
    setError('')
    setPreview(null)
    try {
      const result = await api.post<AiGeneratedPage>('/ai/generate-site', { prompt: trimmed })
      setPreview(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateSite() {
    if (!preview) return
    setSaving(true)
    setError('')
    try {
      const baseSlug = preview.title
        .toLowerCase()
        .replace(/[^a-z0-9а-яёa-z]/gi, '-')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 30) || 'ai-site'
      const slug = `${baseSlug}-${Date.now().toString(36)}`

      const site = await sitesApi.create(preview.title, slug)
      await sitesApi.upsertPage(site.id, {
        title: preview.title,
        slug: '/',
        isHome: true,
        seoTitle: preview.seoTitle,
        seoDesc: preview.seoDesc,
        content: { sections: preview.sections },
      })
      const fullSite = await sitesApi.get(site.id).catch(() => site)
      initFromApiSite(fullSite)
      onCreated(fullSite)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка создания сайта')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: '20px', width: '640px', maxWidth: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>✨</div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#111' }}>AI — Создать сайт</div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '1px' }}>Опишите сайт — AI сгенерирует блоки и тексты</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#999', cursor: 'pointer', padding: '4px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <textarea
              autoFocus
              value={prompt}
              onChange={e => { setPrompt(e.target.value); setError('') }}
              onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleGenerate() }}
              placeholder="Например: Лендинг для онлайн-школы английского. Аудитория — взрослые 25–45 лет. Разделы: преимущества, форматы занятий, цены, запись на пробный урок."
              rows={4}
              style={{ width: '100%', padding: '12px 14px', border: `1px solid ${error ? '#ef4444' : '#e5e7eb'}`, borderRadius: '10px', fontSize: '14px', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', outline: 'none', color: '#111' }}
            />
            {error && <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px' }}>{error}</div>}
          </div>

          {!preview && !loading && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Примеры</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {AI_EXAMPLE_PROMPTS.map((ex, i) => (
                  <button key={i} onClick={() => setPrompt(ex)}
                    style={{ padding: '5px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '20px', fontSize: '12px', color: '#555', cursor: 'pointer' }}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid #e5e7eb', borderTopColor: '#5b6af0', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '6px' }}>Генерируем сайт...</div>
              <div style={{ fontSize: '13px', color: '#888' }}>AI создаёт блоки, тексты и структуру</div>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {preview && !loading && (
            <div style={{ background: '#f0f9f0', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '18px', marginBottom: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#111' }}>Готово!</div>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', marginBottom: '4px' }}>{preview.title}</div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                {preview.sections.length} секций · {preview.sections.reduce((n, s) => n + s.blocks.length, 0)} блоков
              </div>
              {preview.seoDesc && (
                <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', borderTop: '1px solid #bbf7d0', paddingTop: '8px', marginTop: '8px' }}>{preview.seoDesc}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
          {preview ? (
            <>
              <button onClick={() => { setPreview(null); setPrompt('') }} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
                Попробовать снова
              </button>
              <button
                onClick={handleCreateSite}
                disabled={saving}
                style={{ padding: '9px 22px', background: saving ? '#a5b4fc' : '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px' }}
              >
                {saving ? 'Создание...' : '✓ Создать сайт'}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
                Отмена
              </button>
              <button
                onClick={handleGenerate}
                disabled={loading || prompt.trim().length < 10}
                style={{ padding: '9px 22px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: loading || prompt.trim().length < 10 ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: prompt.trim().length < 10 ? 0.6 : 1 }}
              >
                {loading ? '...' : '✨ Сгенерировать'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Rename site modal ─────────────────────────────────────────────────────────

function RenameSiteModal({ site, onRenamed, onClose }: RenameSiteModalProps) {
  const [name, setName] = useState(site.name)
  const [slug, setSlug] = useState(site.slug)
  const [slugChanged, setSlugChanged] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleSlugChange(val: string) {
    const clean = val.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
    setSlug(clean)
    setSlugChanged(clean !== site.slug)
  }

  async function handleSave() {
    if (!name.trim()) return
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug) && slug.length < 2) {
      setError('Поддомен должен содержать минимум 2 символа: латинские буквы, цифры, дефис')
      return
    }
    setLoading(true)
    try {
      const updated = await sitesApi.update(site.id, { name: name.trim(), slug })
      onRenamed(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка')
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '420px', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 700 }}>Настройки сайта</h3>

        <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px', display: 'block' }}>Название</label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave() }}
          style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '16px' }}
        />

        <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px', display: 'block' }}>Адрес сайта</label>
        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '6px' }}>
          <input
            type="text"
            value={slug}
            onChange={e => handleSlugChange(e.target.value)}
            style={{ flex: 1, padding: '10px 12px', border: 'none', fontSize: '14px', outline: 'none', fontFamily: 'monospace' }}
          />
          <span style={{ padding: '10px 12px', background: '#f9fafb', fontSize: '13px', color: '#888', borderLeft: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>.mybst.ru</span>
        </div>
        {slugChanged && (
          <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            ⚠ URL изменится. Старые ссылки перестанут работать.
          </div>
        )}

        {error && <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '8px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Отмена</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: '8px 16px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}>
            {loading ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Workspace row ─────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = { OWNER: 'Владелец', ADMIN: 'Админ', EDITOR: 'Редактор', VIEWER: 'Читатель' }
const ROLE_COLORS: Record<string, string> = { OWNER: '#7c3aed', ADMIN: '#2563eb', EDITOR: '#16a34a', VIEWER: '#6b7280' }

function WorkspaceRow({ item, onManage, onDelete }: { item: WorkspaceListItem; onManage: () => void; onDelete: () => void }) {
  const { workspace, role } = item
  const isOwner = role === 'OWNER'
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ width: 44, height: 44, borderRadius: '10px', background: '#5b6af0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
        {workspace.name[0]?.toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <span style={{ fontSize: '15px', fontWeight: 700, color: '#111' }}>{workspace.name}</span>
          <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: `${ROLE_COLORS[role]}18`, color: ROLE_COLORS[role] }}>
            {ROLE_LABELS[role] ?? role}
          </span>
        </div>
        <div style={{ fontSize: '13px', color: '#888' }}>
          {workspace.slug} · {workspace._count.members} участников · {workspace._count.sites} сайтов
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button onClick={onManage} style={{ padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: '#333' }}>
          Управлять
        </button>
        {isOwner && (
          <button onClick={onDelete} style={{ padding: '7px 14px', background: 'none', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: '#ef4444' }}>
            Удалить
          </button>
        )}
      </div>
    </div>
  )
}

// ── Create workspace modal ─────────────────────────────────────────────────────

function CreateWorkspaceModal({ onCreated, onClose }: { onCreated: (item: WorkspaceListItem) => void; onClose: () => void }) {
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'my-workspace'

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) { setError('Введите название'); return }
    setLoading(true); setError('')
    try {
      const uniqueSlug = `${slug}-${Date.now().toString(36)}`
      await workspacesApi.create(name.trim(), uniqueSlug)
      const list = await workspacesApi.list() as WorkspaceListItem[]
      const created = list.find(w => w.workspace.slug === uniqueSlug)
      if (created) onCreated(created)
      else onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка')
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: 460, maxWidth: '95vw', padding: '32px', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Новое рабочее пространство</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleCreate}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Название</label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={e => { setName(e.target.value); setError('') }}
            placeholder="Моя компания"
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 8 }}
          />
          <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>Адрес: <span style={{ fontFamily: 'monospace' }}>{slug}</span></div>
          {error && <div style={{ fontSize: 13, color: '#ef4444', marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Отмена</button>
            <button type="submit" disabled={loading || !name.trim()} style={{ padding: '9px 20px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Создание...' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Workspace manage modal ─────────────────────────────────────────────────────

// ── §43 RBAC Permission Matrix ────────────────────────────────────────────────

const PERMISSION_ROWS = [
  { key: 'createSite',    label: 'Создавать сайты' },
  { key: 'editSite',      label: 'Редактировать сайты' },
  { key: 'deleteSite',    label: 'Удалять сайты' },
  { key: 'publishSite',   label: 'Публиковать сайты' },
  { key: 'inviteMembers', label: 'Приглашать участников' },
  { key: 'manageRoles',   label: 'Управлять ролями' },
  { key: 'viewAnalytics', label: 'Просматривать аналитику' },
  { key: 'manageBilling', label: 'Управлять биллингом' },
]

const ROLE_PERMISSIONS: Record<string, Record<string, boolean>> = {
  OWNER:  { createSite: true,  editSite: true,  deleteSite: true,  publishSite: true,  inviteMembers: true,  manageRoles: true,  viewAnalytics: true,  manageBilling: true  },
  ADMIN:  { createSite: true,  editSite: true,  deleteSite: true,  publishSite: true,  inviteMembers: true,  manageRoles: false, viewAnalytics: true,  manageBilling: false },
  MEMBER: { createSite: false, editSite: true,  deleteSite: false, publishSite: true,  inviteMembers: false, manageRoles: false, viewAnalytics: true,  manageBilling: false },
  VIEWER: { createSite: false, editSite: false, deleteSite: false, publishSite: false, inviteMembers: false, manageRoles: false, viewAnalytics: true,  manageBilling: false },
}

function WorkspacePermissionMatrix() {
  const [expanded, setExpanded] = useState(false)

  return (
    <div style={{ marginBottom: 24 }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: expanded ? 10 : 0 }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          🔐 Матрица прав §43
        </span>
        <span style={{ fontSize: 12, color: '#999' }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>Действие</th>
                {['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].map(role => (
                  <th key={role} style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 700, color: ROLE_COLORS[role as keyof typeof ROLE_COLORS] ?? '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                    {ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_ROWS.map((row, i) => (
                <tr key={row.key} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '7px 12px', color: '#374151', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{row.label}</td>
                  {['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'].map(role => (
                    <td key={role} style={{ padding: '7px 14px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                      {ROLE_PERMISSIONS[role]?.[row.key]
                        ? <span style={{ color: '#16a34a', fontSize: 14 }}>✓</span>
                        : <span style={{ color: '#d1d5db', fontSize: 14 }}>—</span>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function WorkspaceManageModal({ item, onClose, onDeleted }: { item: WorkspaceListItem; onClose: () => void; onDeleted: () => void }) {
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'EDITOR' | 'VIEWER'>('EDITOR')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteMsg, setInviteMsg] = useState('')

  const canManage = item.role === 'OWNER' || item.role === 'ADMIN'

  useEffect(() => {
    workspacesApi.get(item.workspace.id).then(d => setDetail(d as unknown as WorkspaceDetail)).catch(() => {})
  }, [item.workspace.id])

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviteLoading(true); setInviteMsg('')
    try {
      await workspacesApi.invite(item.workspace.id, inviteEmail.trim(), inviteRole)
      const fresh = await workspacesApi.get(item.workspace.id)
      setDetail(fresh as unknown as WorkspaceDetail)
      setInviteEmail('')
      setInviteMsg('Участник добавлен!')
    } catch (e) {
      setInviteMsg(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setInviteLoading(false)
      setTimeout(() => setInviteMsg(''), 3000)
    }
  }

  async function handleRemove(userId: string) {
    if (!confirm('Удалить участника из пространства?')) return
    try {
      await workspacesApi.removeMember(item.workspace.id, userId)
      setDetail(prev => prev ? { ...prev, members: prev.members.filter(m => m.user.id !== userId) } : prev)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: 560, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', padding: '32px', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{item.workspace.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 28 }}>/{item.workspace.slug}</div>

        {/* Members */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Участники</div>
          {!detail ? (
            <div style={{ color: '#888', fontSize: 13 }}>Загрузка...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {detail.members.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#f9fafb', borderRadius: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#555', flexShrink: 0 }}>
                    {(m.user.name ?? m.user.email)[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.user.name ?? m.user.email}</div>
                    {m.user.name && <div style={{ fontSize: 12, color: '#888' }}>{m.user.email}</div>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: `${ROLE_COLORS[m.role]}18`, color: ROLE_COLORS[m.role], flexShrink: 0 }}>
                    {ROLE_LABELS[m.role] ?? m.role}
                  </span>
                  {canManage && m.role !== 'OWNER' && (
                    <button onClick={() => handleRemove(m.user.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: '2px 4px', flexShrink: 0 }}
                      title="Удалить участника">×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* §43 RBAC Permission Matrix */}
        <WorkspacePermissionMatrix />

        {/* Invite form */}
        {canManage && (
          <form onSubmit={handleInvite}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Пригласить участника</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none' }}
              />
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as 'ADMIN' | 'EDITOR' | 'VIEWER')}
                style={{ padding: '9px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer' }}
              >
                <option value="ADMIN">Админ</option>
                <option value="EDITOR">Редактор</option>
                <option value="VIEWER">Читатель</option>
              </select>
              <button type="submit" disabled={inviteLoading || !inviteEmail.trim()} style={{ padding: '9px 16px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: inviteLoading ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                {inviteLoading ? '...' : 'Добавить'}
              </button>
            </div>
            {inviteMsg && <div style={{ fontSize: 12, color: inviteMsg.includes('!') ? '#16a34a' : '#ef4444', marginTop: 6 }}>{inviteMsg}</div>}
          </form>
        )}

        {/* Delete workspace (owner only) */}
        {item.role === 'OWNER' && (
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid #f3f4f6' }}>
            <button
              onClick={async () => {
                if (!confirm(`Удалить пространство «${item.workspace.name}»? Это действие нельзя отменить.`)) return
                try {
                  await workspacesApi.delete(item.workspace.id)
                  onDeleted()
                } catch (e) {
                  alert(e instanceof Error ? e.message : 'Ошибка')
                }
              }}
              style={{ padding: '8px 16px', background: 'none', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#ef4444' }}
            >
              Удалить пространство
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
